import { useForm, EVALUATION_QUESTIONS, QUESTION_WEIGHTS, MAX_SCORE, DOCUMENT_TYPES, STEP_TITLES, UNIONCASA_IBAN, SINDACATI_INQUILINI } from "../../context/FormContext";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Info, MapPin, Calculator, Loader2, CheckCircle2, AlertTriangle, RotateCw, Download, FileCheck2, Lock } from "lucide-react";
import FileUploadField from "./FileUploadField";
import ContractGeneratorModal from "./ContractGeneratorModal";
import api, { API, formatEuro } from "../../lib/api";
import { checkEmail } from "../../lib/emailValidation";
import { toast } from "sonner";

function SectionHeader({ overline, title, description }) {
  return (
    <div className="mb-8">
      {overline && <div className="overline mb-3">{overline}</div>}
      <h2 className="font-serif text-3xl md:text-4xl font-light text-slate-900 tracking-tight mb-3">{title}</h2>
      {description && <p className="text-slate-600 leading-relaxed max-w-2xl">{description}</p>}
    </div>
  );
}

function InfoBox({ children, testId }) {
  return (
    <div className="flex gap-3 bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-700 leading-relaxed" data-testid={testId}>
      <Info size={18} className="text-slate-500 shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

// ------------------- STEP 1 -------------------
export function Step1Intro() {
  const { data, setField } = useForm();
  const tipo = data.richiedente?.tipo || "";
  const [agenziaEmailTouched, setAgenziaEmailTouched] = useState(false);
  const agenziaEmailCheck = checkEmail(data.richiedente?.email);
  const showAgenziaEmailError =
    tipo === "agenzia" &&
    agenziaEmailTouched &&
    !agenziaEmailCheck.ok &&
    (data.richiedente?.email || "").length > 0;
  return (
    <div className="animate-fade-in-up">
      <SectionHeader
        overline="Nuova Richiesta"
        title="Attestazione per Canone Concordato"
        description="Accordo locale per la città di Milano. Attestazione di rispondenza di contratto a canone concordato."
      />
      <div className="space-y-6">
        <p className="text-lg text-slate-700 leading-relaxed">
          Benvenuta/o, compila il modulo per ricevere la tua attestazione.
        </p>

        <InfoBox testId="intro-info">
          Il modulo è diviso in 12 step. Puoi salvare una bozza in qualsiasi momento e
          riprendere più tardi. Preparati: ti serviranno la visura catastale aggiornata
          con indicazione dei metri quadri e degli intestatari, l&apos;APE in corso di
          validità, la planimetria e i documenti del locatore e conduttore.
        </InfoBox>

        {/* --- Richiedente: chi sta compilando la pratica --- */}
        <div className="border border-slate-200 rounded-lg p-5 bg-white space-y-4" data-testid="richiedente-card">
          <div>
            <div className="overline mb-2">Prima di iniziare</div>
            <h3 className="font-serif text-xl text-slate-900">Chi sta compilando la pratica?</h3>
            <p className="text-sm text-slate-500 mt-1">Ci serve per gestire correttamente le comunicazioni interne.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3" data-testid="richiedente-tipo-group">
            {[
              { v: "locatore", label: "Sono il locatore", desc: "Il proprietario dell'immobile" },
              { v: "agenzia", label: "Sono un professionista incaricato", desc: "Compilo per conto del locatore" },
            ].map((o) => {
              const active = tipo === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setField("richiedente.tipo", o.v)}
                  className={`text-left border rounded-md p-4 transition-colors ${
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
                  }`}
                  data-testid={`richiedente-tipo-${o.v}`}
                >
                  <div className="text-sm font-semibold">{o.label}</div>
                  <div className={`text-xs mt-1 ${active ? "text-slate-200" : "text-slate-500"}`}>{o.desc}</div>
                </button>
              );
            })}
          </div>

          {tipo === "locatore" && (
            <div className="pt-2 border-t border-slate-100" data-testid="richiedente-locatore-info">
              <p className="text-sm text-slate-600 leading-relaxed">
                Perfetto. Nello <strong>Step 2</strong> ti chiederemo i tuoi dati (nome ed email) — li useremo sia per l&apos;attestazione sia per le comunicazioni con te.
              </p>
            </div>
          )}

          {tipo === "agenzia" && (
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <div>
                <Label htmlFor="richiedente-email">
                  Email di riferimento del Professionista incaricato <span className="text-red-700">*</span>
                </Label>
                <Input
                  id="richiedente-email"
                  type="email"
                  placeholder="info@studioprofessionista.it"
                  value={data.richiedente?.email || ""}
                  onChange={(e) => setField("richiedente.email", e.target.value)}
                  onBlur={() => setAgenziaEmailTouched(true)}
                  data-testid="richiedente-email"
                  className={`mt-1.5 ${showAgenziaEmailError ? "border-red-400 focus-visible:ring-red-200" : ""}`}
                  aria-invalid={showAgenziaEmailError || undefined}
                />
                {showAgenziaEmailError ? (
                  <p className="text-xs text-red-700 mt-1 font-medium" data-testid="richiedente-email-error">
                    {agenziaEmailCheck.reason}
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    In modalità Professionista incaricato <strong>tutte</strong> le comunicazioni Unioncasa
                    (conferma, richieste modifiche, attestazione firmata) vengono inviate
                    a questo indirizzo — <strong>non</strong> al locatore.
                  </p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-3" data-testid="richiedente-agenzia-fields">
                <div>
                  <Label htmlFor="richiedente-agenzia-nome">
                    Nome Professionista incaricato / Studio <span className="text-red-700">*</span>
                  </Label>
                  <Input
                    id="richiedente-agenzia-nome"
                    placeholder="Es. Immobiliare Milano S.r.l."
                    value={data.richiedente?.agenzia_nome || ""}
                    onChange={(e) => setField("richiedente.agenzia_nome", e.target.value)}
                    data-testid="richiedente-agenzia-nome"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="richiedente-agenzia-referente">
                    Nome referente <span className="text-red-700">*</span>
                  </Label>
                  <Input
                    id="richiedente-agenzia-referente"
                    placeholder="Es. Laura Bianchi"
                    value={data.richiedente?.agenzia_referente || ""}
                    onChange={(e) => setField("richiedente.agenzia_referente", e.target.value)}
                    data-testid="richiedente-agenzia-referente"
                    className="mt-1.5"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-4 pt-4">
          {["Dati immobile", "Valutazione 25 criteri", "Canone calcolato"].map((l, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-4 bg-white">
              <div className="overline mb-2">0{i + 1}</div>
              <div className="text-sm font-medium text-slate-900">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------- STEP 2 -------------------
export function Step2Locatore() {
  const { data, setLocatori } = useForm();
  const locatori = data.locatori && data.locatori.length ? data.locatori : [{ nome: "", email: "" }];
  const [emailTouched, setEmailTouched] = useState({}); // { [index]: bool }

  const setEntry = (i, patch) => {
    const next = locatori.map((x, j) => (j === i ? { ...x, ...patch } : x));
    setLocatori(next);
  };
  const addOne = () => setLocatori([...locatori, { nome: "", email: "" }]);
  const removeOne = (i) => {
    if (locatori.length <= 1) return;
    setLocatori(locatori.filter((_, j) => j !== i));
    setEmailTouched((t) => { const c = { ...t }; delete c[i]; return c; });
  };

  return (
    <div className="animate-fade-in-up">
      <SectionHeader
        overline="Step 2"
        title="Dati Proprietario/a Immobile"
        description="Locatore/Locatrice — colui o colei che concede in locazione l'immobile. Se sono più di uno, aggiungili tutti."
      />
      <div className="space-y-6 max-w-xl" data-testid="locatori-list">
        {locatori.map((p, i) => {
          const emailCheck = checkEmail(p.email);
          const showErr = !!emailTouched[i] && !emailCheck.ok && (p.email || "").length > 0;
          return (
            <div
              key={i}
              className="relative border border-slate-200 rounded-lg p-5 bg-white"
              data-testid={`locatore-card-${i}`}
            >
              <div className="flex items-center justify-between mb-4">
                <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                  Locatore/Locatrice n. {i + 1}
                </p>
                {locatori.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeOne(i)}
                    className="text-xs text-red-700 hover:underline"
                    data-testid={`locatore-remove-${i}`}
                  >
                    Rimuovi
                  </button>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor={`loc-nome-${i}`}>Nome e Cognome <span className="text-red-700">*</span></Label>
                  <Input
                    id={`loc-nome-${i}`}
                    placeholder="Mario Rossi"
                    value={p.nome || ""}
                    onChange={(e) => setEntry(i, { nome: e.target.value })}
                    data-testid={`locatore-nome-${i}`}
                    className="mt-1.5"
                  />
                  {(() => {
                    const v = (p.nome || "").trim();
                    if (!v) return null;
                    const parts = v.split(/\s+/).filter((t) => t.length >= 2);
                    if (parts.length < 2) {
                      return (
                        <p className="text-xs text-red-700 mt-1.5 font-medium" data-testid={`locatore-nome-error-${i}`}>
                          Inserisci sia il Nome sia il Cognome.
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div>
                  <Label htmlFor={`loc-email-${i}`}>Indirizzo email <span className="text-red-700">*</span></Label>
                  <Input
                    id={`loc-email-${i}`}
                    type="email"
                    placeholder="mario.rossi@esempio.it"
                    value={p.email || ""}
                    onChange={(e) => setEntry(i, { email: e.target.value })}
                    onBlur={() => setEmailTouched((t) => ({ ...t, [i]: true }))}
                    data-testid={`locatore-email-${i}`}
                    className={`mt-1.5 ${showErr ? "border-red-400 focus-visible:ring-red-200" : ""}`}
                    aria-invalid={showErr || undefined}
                  />
                  {showErr ? (
                    <p className="text-xs text-red-700 mt-1.5 font-medium" data-testid={`locatore-email-error-${i}`}>
                      {emailCheck.reason}
                    </p>
                  ) : i === 0 ? (
                    <p className="text-xs text-slate-500 mt-1.5">
                      Manderemo qui il risultato della valutazione.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
        <button
          type="button"
          onClick={addOne}
          className="text-sm text-slate-900 font-medium underline underline-offset-4 hover:no-underline"
          data-testid="locatore-add"
        >
          + Aggiungi un altro locatore/locatrice
        </button>
      </div>
    </div>
  );
}

// ------------------- STEP 3 -------------------
export function Step3Conduttore() {
  const { data, setConduttori } = useForm();
  const conduttori = data.conduttori && data.conduttori.length ? data.conduttori : [{ nome: "", email: "" }];
  // Email obbligatoria solo quando a compilare è il locatore (Step 1).
  const emailRequired = String(data?.richiedente?.tipo || "").toLowerCase() === "locatore";
  // Email dei locatori (lowercased) usate per impedire la riutilizzazione sullo
  // stesso conduttore: la mail di firma andrà al conduttore e deve essere
  // raggiungibile dal conduttore stesso, non dal locatore.
  const locatoreEmails = useMemo(() => {
    const locs = Array.isArray(data.locatori) && data.locatori.length ? data.locatori : [data.locatore || {}];
    return new Set(
      locs
        .map((l) => String(l?.email || "").trim().toLowerCase())
        .filter(Boolean)
    );
  }, [data.locatori, data.locatore]);

  const setEntry = (i, patch) => {
    const next = conduttori.map((x, j) => (j === i ? { ...x, ...patch } : x));
    setConduttori(next);
  };
  const addOne = () => setConduttori([...conduttori, { nome: "", email: "" }]);
  const removeOne = (i) => {
    if (conduttori.length <= 1) return;
    setConduttori(conduttori.filter((_, j) => j !== i));
  };

  return (
    <div className="animate-fade-in-up">
      <SectionHeader
        overline="Step 3"
        title="Dati Conduttore/Conduttrice"
        description="Colui o colei che prende in locazione l'immobile. Se sono più di uno, aggiungili tutti."
      />
      <div className="space-y-6 max-w-xl" data-testid="conduttori-list">
        {conduttori.map((p, i) => (
          <div
            key={i}
            className="relative border border-slate-200 rounded-lg p-5 bg-white"
            data-testid={`conduttore-card-${i}`}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                Conduttore/Conduttrice n. {i + 1}
              </p>
              {conduttori.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeOne(i)}
                  className="text-xs text-red-700 hover:underline"
                  data-testid={`conduttore-remove-${i}`}
                >
                  Rimuovi
                </button>
              )}
            </div>
            <div>
              <Label htmlFor={`cond-nome-${i}`}>Nome e Cognome <span className="text-red-700">*</span></Label>
              <Input
                id={`cond-nome-${i}`}
                placeholder="Lucia Bianchi"
                value={p.nome || ""}
                onChange={(e) => setEntry(i, { nome: e.target.value })}
                data-testid={`conduttore-nome-${i}`}
                className="mt-1.5"
              />
              {(() => {
                const v = (p.nome || "").trim();
                if (!v) return null;
                const parts = v.split(/\s+/).filter((t) => t.length >= 2);
                if (parts.length < 2) {
                  return (
                    <p className="text-xs text-red-700 mt-1.5 font-medium" data-testid={`conduttore-nome-error-${i}`}>
                      Inserisci sia il Nome sia il Cognome.
                    </p>
                  );
                }
                return null;
              })()}
            </div>
            <div className="mt-4">
              <Label htmlFor={`cond-email-${i}`}>
                Email {emailRequired ? (
                  <span className="text-red-700">*</span>
                ) : (
                  <span className="text-slate-400 text-xs font-normal">(facoltativa)</span>
                )}
              </Label>
              <Input
                id={`cond-email-${i}`}
                type="email"
                placeholder="lucia.bianchi@esempio.it"
                value={p.email || ""}
                onChange={(e) => setEntry(i, { email: e.target.value })}
                data-testid={`conduttore-email-${i}`}
                className="mt-1.5"
              />
              {(() => {
                const v = (p.email || "").trim().toLowerCase();
                if (!v) {
                  if (emailRequired) {
                    return (
                      <p className="text-xs text-red-700 mt-1 font-medium" data-testid={`conduttore-email-error-${i}`}>
                        Email obbligatoria quando la pratica è compilata dal locatore.
                      </p>
                    );
                  }
                  return (
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Se inserita, dopo l&apos;approvazione di Unioncasa il conduttore riceverà qui un link
                      privato per visualizzare e firmare il contratto online. In alternativa potrai
                      condividere manualmente il link di firma dalla tua dashboard.
                    </p>
                  );
                }
                const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
                if (!emailValid) {
                  return (
                    <p className="text-xs text-red-700 mt-1 font-medium" data-testid={`conduttore-email-error-${i}`}>
                      Indirizzo email non valido.
                    </p>
                  );
                }
                if (locatoreEmails.has(v)) {
                  return (
                    <p className="text-xs text-red-700 mt-1 font-medium" data-testid={`conduttore-email-error-${i}`}>
                      L&apos;email del conduttore deve essere diversa da quella del locatore.
                    </p>
                  );
                }
                // Check duplicata tra conduttori
                const dup = conduttori
                  .filter((_, j) => j !== i)
                  .some((c) => (c?.email || "").trim().toLowerCase() === v);
                if (dup) {
                  return (
                    <p className="text-xs text-red-700 mt-1 font-medium" data-testid={`conduttore-email-error-${i}`}>
                      Questa email è già usata da un altro conduttore inserito.
                    </p>
                  );
                }
                return (
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Dopo l&apos;approvazione di Unioncasa riceverà qui un link privato per
                    visualizzare e firmare il contratto online.
                  </p>
                );
              })()}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addOne}
          className="text-sm text-slate-900 font-medium underline underline-offset-4 hover:no-underline"
          data-testid="conduttore-add"
        >
          + Aggiungi un altro conduttore/conduttrice
        </button>
      </div>
    </div>
  );
}

// ------------------- STEP 4 -------------------
export function Step4Zona() {
  const { data, setField, setData } = useForm();
  const zoneOptions = [
    { v: 1, label: "Zona 1", color: "#ec4899", desc: "Rosa" },
    { v: 2, label: "Zona 2", color: "#16a34a", desc: "Verde" },
    { v: 3, label: "Zona 3", color: "#eab308", desc: "Gialla" },
    { v: 4, label: "Zona 4", color: "#f97316", desc: "Arancione" },
  ];

  // Auto-detect zona state machine: idle | loading | success | error
  const [detect, setDetect] = useState({ status: "idle", error: null, address: null, lat: null, lon: null });
  const debounceRef = useRef(null);
  const lastQueryRef = useRef("");
  const reqIdRef = useRef(0);

  // Autocomplete state for the "Via" input
  const [suggest, setSuggest] = useState({ items: [], loading: false, open: false, active: -1 });
  const suggestDebRef = useRef(null);
  const suggestReqIdRef = useRef(0);
  const suggestBoxRef = useRef(null);
  const skipSuggestRef = useRef(false);  // skip next suggestion fetch (after selecting an option)

  const via = data.immobile.via?.trim() || "";
  const civico = data.immobile.civico?.trim() || "";
  const cap = data.immobile.cap?.trim() || "";
  const capValid = /^201\d{2}$/.test(cap);

  // Run the API call
  async function runDetection() {
    const reqId = ++reqIdRef.current;
    setDetect({ status: "loading", error: null, address: null, lat: null, lon: null });
    try {
      const { data: res } = await api.post("/geocode-zone", { via, civico, cap });
      if (reqId !== reqIdRef.current) return; // stale
      // Auto-populate CAP field if empty and backend returned one
      if (res?.cap && (!cap || cap !== res.cap)) {
        setField("immobile.cap", res.cap);
      }
      // Auto-populate città and provincia when the address resolves to Milano
      if (res?.city && res.city.toLowerCase() === "milano") {
        setField("immobile.citta", "Milano");
        setField("immobile.provincia", "MI");
      }
      // Enforce Milano-only for manual entry
      if (res?.error === "not_milano") {
        setField("zona", null);
        setDetect({
          status: "error",
          error: "not_milano",
          address: res?.display_name || null,
          lat: null,
          lon: null,
        });
        return;
      }
      if (res?.ok && res.zona) {
        setField("zona", res.zona);
        setDetect({ status: "success", error: null, address: res.display_name, lat: res.lat, lon: res.lon });
      } else {
        setField("zona", null);
        setDetect({ status: "error", error: res?.error || "address_not_found", address: res?.display_name || null, lat: null, lon: null });
      }
    } catch {
      if (reqId !== reqIdRef.current) return;
      setField("zona", null);
      setDetect({ status: "error", error: "server_error", address: null, lat: null, lon: null });
    }
  }

  // Debounced trigger on address change
  useEffect(() => {
    const key = `${via.toLowerCase()}|${civico.toLowerCase()}|${cap}`;
    if (key === lastQueryRef.current && (detect.status === "success" || detect.status === "loading")) {
      return;
    }
    // Trigger detection as soon as via+civico are entered (CAP will be auto-filled
    // by backend response). Also re-trigger if the user manually edits CAP.
    if (!via || !civico) {
      return;
    }
    lastQueryRef.current = key;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runDetection();
    }, 900);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [via, civico, cap]);

  const showResult = detect.status !== "idle" || !!data.zona;
  const detectedZone = data.zona ? zoneOptions.find((z) => z.v === data.zona) : null;

  // ---- Autocomplete logic ----
  async function fetchSuggestions(q) {
    const reqId = ++suggestReqIdRef.current;
    setSuggest((s) => ({ ...s, loading: true, open: true }));
    try {
      const { data: res } = await api.get("/geocode-suggest", { params: { q } });
      if (reqId !== suggestReqIdRef.current) return;
      const items = (res?.results || []).slice(0, 6);
      setSuggest({ items, loading: false, open: items.length > 0, active: -1 });
    } catch {
      if (reqId !== suggestReqIdRef.current) return;
      setSuggest({ items: [], loading: false, open: false, active: -1 });
    }
  }

  function handleViaChange(value) {
    setField("immobile.via", value);
    if (skipSuggestRef.current) {
      skipSuggestRef.current = false;
      return;
    }
    if (suggestDebRef.current) clearTimeout(suggestDebRef.current);
    const v = value.trim();
    if (v.length < 2) {
      setSuggest({ items: [], loading: false, open: false, active: -1 });
      return;
    }
    suggestDebRef.current = setTimeout(() => fetchSuggestions(v), 200);
  }

  function pickSuggestion(item) {
    skipSuggestRef.current = true;
    const newVia = item.via || "";
    const newCivico = item.civico || "";
    const newCap = item.cap || "";
    setData((prev) => ({
      ...prev,
      zona: item.zona || null,
      immobile: {
        ...prev.immobile,
        via: newVia || prev.immobile.via,
        civico: newCivico || prev.immobile.civico,
        cap: newCap || prev.immobile.cap,
        citta: "Milano",
        provincia: "MI",
      },
    }));
    // Short-circuit the detection only when the suggestion carries full
    // coordinates (classic Photon/Nominatim result). The local street
    // index returns `lat/lon = null`: let the useEffect fire so that
    // `/geocode-zone` resolves exact coords and the map updates.
    if (item.zona && item.lat != null && item.lon != null) {
      const finalVia = (newVia || via).toLowerCase();
      const finalCivico = (newCivico || civico).toLowerCase();
      const finalCap = newCap || cap;
      lastQueryRef.current = `${finalVia}|${finalCivico}|${finalCap}`;
      setDetect({ status: "success", error: null, address: item.label, lat: item.lat, lon: item.lon });
    }
    setSuggest({ items: [], loading: false, open: false, active: -1 });
  }

  function handleViaKeyDown(e) {
    if (!suggest.open || suggest.items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggest((s) => ({ ...s, active: Math.min(s.active + 1, s.items.length - 1) }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggest((s) => ({ ...s, active: Math.max(s.active - 1, 0) }));
    } else if (e.key === "Enter") {
      if (suggest.active >= 0) {
        e.preventDefault();
        pickSuggestion(suggest.items[suggest.active]);
      }
    } else if (e.key === "Escape") {
      setSuggest((s) => ({ ...s, open: false }));
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(e.target)) {
        setSuggest((s) => ({ ...s, open: false }));
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // ---- Auto-propagate Piano/Ascensore to Step 7 (elevator) and Step 9 (basement) ----
  const piano = data.immobile.piano;
  const ascensore = !!data.immobile.ascensore;
  // La presenza dell'ascensore è valutabile SOLO dal 2° piano in su.
  // Seminterrato / piano terra / 1° piano → ascensore non applicabile ai fini della sub-fascia.
  const isPianoBelowElevator = piano === "seminterrato" || piano === "terra" || piano === "1";
  useEffect(() => {
    // Se il piano non consente ascensore, forza ascensore=false
    if (isPianoBelowElevator && ascensore) {
      setField("immobile.ascensore", false);
    }
    // Question 2 (key "q2") — auto-set to Sì if ascensore is present in Step 4 (checkbox on).
    // La checkbox è già bloccata a false per seminterrato/terra, quindi q2=No in quei casi.
    const q2Should = ascensore ? "si" : "no";
    if (data.valutazione?.q2 !== q2Should) {
      setField("valutazione.q2", q2Should);
    }
    // Step 9 seminterrato flag
    const semShould = piano === "seminterrato" ? "si" : "no";
    if (data.step9?.seminterrato !== semShould) {
      setField("step9.seminterrato", semShould);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piano, ascensore]);

  return (
    <div className="animate-fade-in-up">
      <SectionHeader
        overline="Step 4"
        title="Zona e Indirizzo Immobile"
        description="In quale zona di Milano è situato l'immobile? La zona viene rilevata automaticamente dall'indirizzo."
      />

      <InfoBox testId="zona-info">
        <div className="space-y-1">
          <p className="font-medium text-slate-900">Rilevamento automatico della Zona</p>
          <p>
            Inizia a digitare la <strong>Via</strong> nel campo qui sotto: comparirà un menu con
            i suggerimenti. Selezionando un risultato, <strong>Numero civico</strong> e <strong>CAP</strong> vengono
            compilati automaticamente e la zona viene determinata in base alle coordinate dell'indirizzo.
          </p>
        </div>
      </InfoBox>

      <h3 className="font-serif text-xl mt-8 mb-4 text-slate-900">Indirizzo dell'immobile</h3>

      <div className="mb-4 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-4 py-3 flex gap-3" data-testid="zona-restriction-note">
        <Info size={16} className="text-slate-500 shrink-0 mt-0.5" />
        <div>
          L&apos;attestazione è valida <strong>solo per immobili nel Comune di Milano</strong> (CAP <strong>201xx</strong>).
          Inserendo <strong>Via</strong> e <strong>Numero civico</strong>, il <strong>CAP</strong> viene compilato automaticamente.
        </div>
      </div>

      <div className="grid sm:grid-cols-6 gap-4">
        <div className="sm:col-span-4 relative" ref={suggestBoxRef}>
          <Label>Via / Viale / Piazza <span className="text-red-700">*</span></Label>
          <Input
            value={data.immobile.via}
            onChange={(e) => handleViaChange(e.target.value)}
            onKeyDown={handleViaKeyDown}
            onFocus={() => {
              if (suggest.items.length > 0) setSuggest((s) => ({ ...s, open: true }));
            }}
            placeholder="Inizia a digitare l'indirizzo (min. 3 caratteri)"
            className="mt-1.5"
            data-testid="imm-via"
            autoComplete="off"
          />
          {suggest.open && (
            <div
              className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
              data-testid="via-suggestions"
            >
              {suggest.loading && (
                <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Ricerca indirizzi…
                </div>
              )}
              {!suggest.loading && suggest.items.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500">Nessun risultato a Milano.</div>
              )}
              {!suggest.loading && suggest.items.map((it, idx) => (
                <button
                  key={idx}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSuggestion(it)}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                    idx === suggest.active ? "bg-slate-50" : ""
                  }`}
                  data-testid={`via-suggestion-${idx}`}
                >
                  <div className="font-medium text-slate-900 truncate">
                    {it.via}{it.civico ? `, ${it.civico}` : ""}
                  </div>
                  <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                    {it.zona && (
                      <span className="inline-flex items-center gap-1 shrink-0">
                        <span
                          className="inline-block w-1.5 h-3 rounded-sm"
                          style={{
                            backgroundColor: zoneOptions.find((z) => z.v === it.zona)?.color || "#999",
                          }}
                        />
                        Zona {it.zona}
                      </span>
                    )}
                    <span className="truncate">{it.label}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <Label>Numero Civico <span className="text-red-700">*</span></Label>
          <Input
            value={data.immobile.civico}
            onChange={(e) => setField("immobile.civico", e.target.value)}
            placeholder="12"
            className="mt-1.5"
            data-testid="imm-civico"
          />
        </div>
        <div className="sm:col-span-2">
          <Label>Piano <span className="text-red-700">*</span></Label>
          <Select
            value={data.immobile.piano}
            onValueChange={(v) => setField("immobile.piano", v)}
          >
            <SelectTrigger className="mt-1.5" data-testid="imm-piano">
              <SelectValue placeholder="Scegli il piano" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="seminterrato">Seminterrato</SelectItem>
              <SelectItem value="terra">Piano terra</SelectItem>
              {Array.from({ length: 40 }, (_, i) => i + 1).map((n) => (
                <SelectItem key={n} value={String(n)}>{n}° piano</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label>CAP <span className="text-red-700">*</span></Label>
          <Input
            value={data.immobile.cap}
            onChange={(e) => setField("immobile.cap", e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder="20121"
            className={`mt-1.5 ${data.immobile.cap && !/^201\d{2}$/.test(data.immobile.cap) ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
            data-testid="imm-cap"
            maxLength={5}
          />
          {data.immobile.cap && !/^201\d{2}$/.test(data.immobile.cap) && (
            <p className="text-xs text-red-700 mt-1.5 flex items-start gap-1" data-testid="imm-cap-error">
              <span>⚠</span>
              <span>CAP fuori dalle zone di Milano. Il CAP deve iniziare con 201 (es. 20121–20162).</span>
            </p>
          )}
        </div>
        <div className="sm:col-span-1">
          <Label>Città <span className="text-red-700">*</span></Label>
          <Input
            value={data.immobile.citta}
            readOnly
            disabled
            className="mt-1.5 bg-slate-50 text-slate-500 cursor-not-allowed"
            data-testid="imm-citta"
            title="Bloccato: solo Milano"
          />
        </div>
        <div className="sm:col-span-1">
          <Label>Provincia <span className="text-red-700">*</span></Label>
          <Input
            value={data.immobile.provincia}
            readOnly
            disabled
            className="mt-1.5 bg-slate-50 text-slate-500 cursor-not-allowed"
            data-testid="imm-provincia"
            title="Bloccato: solo MI"
          />
        </div>
      </div>

      {/* Elevator toggle */}
      <div className={`mt-4 flex items-start gap-3 p-3 border rounded-md max-w-md transition-colors ${
        isPianoBelowElevator
          ? "border-slate-200 bg-slate-50 opacity-70"
          : "border-slate-200 bg-white"
      }`} data-testid="imm-ascensore-wrap">
        <Checkbox
          id="imm-ascensore"
          checked={!!data.immobile.ascensore && !isPianoBelowElevator}
          onCheckedChange={(v) => setField("immobile.ascensore", !!v)}
          disabled={isPianoBelowElevator}
          data-testid="imm-ascensore"
          className="mt-1"
        />
        <label
          htmlFor="imm-ascensore"
          className={`${isPianoBelowElevator ? "cursor-not-allowed" : "cursor-pointer"}`}
        >
          <div className={`text-sm font-medium ${isPianoBelowElevator ? "text-slate-500" : "text-slate-900"}`}>
            Presenza di ascensore nell'edificio
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {isPianoBelowElevator
              ? `Non applicabile per ${
                  piano === "seminterrato" ? "il seminterrato"
                    : piano === "terra" ? "il piano terra"
                    : "il 1° piano"
                }: la presenza dell'ascensore è valutabile solo dal 2° piano in su. La domanda relativa allo Step 7 è bloccata su No.`
              : "Se attivato, la domanda 'Presenza di ascensore' nello Step 7 sarà auto-impostata a Sì e bloccata."}
          </div>
        </label>
      </div>

      {/* Auto-detection result panel */}
      <div className="mt-8">
        <Label className="mb-3 block">Zona di Milano</Label>

        {detect.status === "idle" && !data.zona && (
          <div
            className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500"
            data-testid="zona-status-idle"
          >
            Inserisci Via e Numero civico per rilevare automaticamente la zona (il CAP verrà compilato in automatico).
          </div>
        )}

        {detect.status === "loading" && (
          <div
            className="rounded-md border border-slate-200 bg-white px-4 py-4 text-sm text-slate-700 flex items-center gap-3"
            data-testid="zona-status-loading"
          >
            <Loader2 size={18} className="animate-spin text-slate-500" />
            <span>Rilevamento zona in corso…</span>
          </div>
        )}

        {detect.status === "success" && detectedZone && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 overflow-hidden"
            data-testid="zona-status-success"
          >
            <div className="px-4 py-4 flex items-start gap-3">
              <CheckCircle2 size={20} className="text-emerald-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-emerald-900">
                  Zona rilevata: {detectedZone.label} — Area {detectedZone.desc}
                </div>
                {detect.address && (
                  <div className="text-xs text-emerald-800/80 mt-1" data-testid="zona-detected-address">
                    Indirizzo riconosciuto: {detect.address}
                  </div>
                )}
              </div>
              <span
                className="w-3 h-10 rounded-sm shrink-0"
                style={{ backgroundColor: detectedZone.color }}
              />
            </div>
            {detect.lat != null && detect.lon != null && (
              <div className="border-t border-emerald-200 bg-white" data-testid="zona-map-container">
                <iframe
                  key={`${detect.lat}-${detect.lon}`}
                  title="Posizione immobile su mappa"
                  className="w-full h-56 sm:h-64 block border-0"
                  loading="lazy"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${detect.lon - 0.004}%2C${detect.lat - 0.002}%2C${detect.lon + 0.004}%2C${detect.lat + 0.002}&layer=mapnik&marker=${detect.lat}%2C${detect.lon}`}
                />
                <div className="px-3 py-2 text-[11px] text-slate-500 flex items-center justify-between gap-3">
                  <span>Verifica che il pin corrisponda al tuo immobile.</span>
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${detect.lat}&mlon=${detect.lon}#map=18/${detect.lat}/${detect.lon}`}
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-slate-700 hover:text-slate-900"
                    data-testid="zona-map-fullscreen-link"
                  >
                    Apri a schermo intero →
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {detect.status === "error" && (
          <div
            className={`rounded-md border px-4 py-4 ${
              detect.error === "not_milano"
                ? "border-red-300 bg-red-50"
                : "border-amber-300 bg-amber-50"
            }`}
            data-testid="zona-status-error"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                size={20}
                className={`shrink-0 mt-0.5 ${detect.error === "not_milano" ? "text-red-700" : "text-amber-700"}`}
              />
              <div className="flex-1">
                <div className={`text-sm font-medium ${detect.error === "not_milano" ? "text-red-900" : "text-amber-900"}`}>
                  {detect.error === "not_milano"
                    ? "L'indirizzo inserito non è nel Comune di Milano. Sono ammessi solo indirizzi della città di Milano."
                    : detect.error === "civico_not_found"
                    ? "Il numero civico inserito non risulta esistere in questa via. Verifica il numero e riprova."
                    : detect.error === "outside_zones"
                    ? "L'indirizzo è stato trovato ma è fuori dalle 4 zone del Comune di Milano."
                    : detect.error === "server_error"
                      ? "Errore temporaneo del servizio di geocoding. Riprova fra qualche secondo."
                      : "Indirizzo non riconosciuto. Verifica via e numero civico."}
                </div>
                <div className={`text-xs mt-1 ${detect.error === "not_milano" ? "text-red-800/80" : "text-amber-800/80"}`}>
                  {detect.error === "not_milano"
                    ? "Modifica l'indirizzo per inserire una via di Milano."
                    : detect.error === "civico_not_found"
                    ? "Controlla che il numero civico sia corretto (es. 12, 12/A). Alcuni civici non sono ancora mappati — puoi provare col civico più vicino."
                    : "Non è possibile proseguire al passo successivo finché non viene rilevata una zona valida."}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3 h-8"
                  onClick={runDetection}
                  data-testid="zona-retry-btn"
                >
                  <RotateCw size={14} className="mr-1.5" />
                  Riprova
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Visual reference of the 4 zones */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="zona-legend">
          {zoneOptions.map((z) => {
            const active = data.zona === z.v;
            return (
              <div
                key={z.v}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs ${
                  active
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-500"
                }`}
                data-testid={`zona-legend-${z.v}`}
              >
                <span
                  className="w-2.5 h-5 rounded-sm shrink-0"
                  style={{ backgroundColor: z.color }}
                />
                <div>
                  <div className={`font-medium ${active ? "text-white" : "text-slate-700"}`}>{z.label}</div>
                  <div className={`text-[11px] ${active ? "text-slate-200" : "text-slate-400"}`}>{z.desc}</div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 mt-3">
          Rilevamento basato su OpenStreetMap (Photon) e sui poligoni ufficiali dell'Accordo territoriale di Milano.
        </p>
      </div>
    </div>
  );
}

// ------------------- STEP 5 -------------------
export function Step5Catasto() {
  const { data, setField, setData } = useForm();
  const pertinenze = data.catasto.pertinenze || [];
  const pertinenzeIncluse = data.catasto.pertinenze_incluse || [];

  const PERT_TYPES = [
    { v: "cantina", label: "Cantina" },
    { v: "soffitta", label: "Soffitta" },
    { v: "box", label: "Box / Autorimessa" },
    { v: "posto_auto", label: "Posto auto" },
    { v: "posto_auto_ricarica", label: "Posto auto / Box con ricarica elettrica" },
    { v: "superficie_scoperta", label: "Superficie scoperta / Giardino esclusivo" },
  ];

  // Auto-sync: quando i dati catastali dell'appartamento cambiano,
  // aggiorna tutte le pertinenze di tipo "balcone" o "terrazzo" con gli stessi valori
  // (entrambi appartengono alla stessa domanda della valutazione immobile).
  const catFoglio = data.catasto.foglio || "";
  const catParticella = data.catasto.particella || "";
  const catSubalterno = data.catasto.subalterno || "";
  useEffect(() => {
    const perts = data.catasto.pertinenze || [];
    let changed = false;
    const next = perts.map((p) => {
      if ((p.tipo === "balcone" || p.tipo === "terrazzo") && (
        p.foglio !== catFoglio ||
        p.particella !== catParticella ||
        p.subalterno !== catSubalterno
      )) {
        changed = true;
        return { ...p, foglio: catFoglio, particella: catParticella, subalterno: catSubalterno };
      }
      return p;
    });
    if (changed) {
      setData((prev) => ({ ...prev, catasto: { ...prev.catasto, pertinenze: next } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catFoglio, catParticella, catSubalterno]);

  function addPertinenza() {
    const next = [...pertinenze, { id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`, tipo: "cantina", foglio: "", particella: "", subalterno: "", mq: "" }];
    setData((prev) => ({ ...prev, catasto: { ...prev.catasto, pertinenze: next } }));
  }

  function removePertinenza(id) {
    const next = pertinenze.filter((p) => p.id !== id);
    setData((prev) => ({ ...prev, catasto: { ...prev.catasto, pertinenze: next } }));
  }

  function updatePertinenza(id, field, value) {
    const next = pertinenze.map((p) => {
      if (p.id !== id) return p;
      const updated = { ...p, [field]: value };
      // Se sto cambiando il tipo a "balcone" o "terrazzo", copio automaticamente
      // i dati catastali dell'appartamento (stessa domanda in valutazione).
      if (field === "tipo" && (value === "balcone" || value === "terrazzo")) {
        updated.foglio = catFoglio;
        updated.particella = catParticella;
        updated.subalterno = catSubalterno;
      }
      return updated;
    });
    setData((prev) => ({ ...prev, catasto: { ...prev.catasto, pertinenze: next } }));
  }

  return (
    <div className="animate-fade-in-up">
      <SectionHeader overline="Step 5" title="Estremi Catastali" description="Inserisci gli estremi catastali dell'appartamento e, se diversi, quelli delle pertinenze (cantina, box, ecc.)." />

      <h3 className="font-serif text-lg text-slate-900 mb-3">Appartamento</h3>
      <div className="grid sm:grid-cols-4 gap-4 max-w-3xl">
        {[
          { k: "foglio", label: "Foglio", testId: "cat-foglio" },
          { k: "particella", label: "Particella / Mappale", testId: "cat-particella" },
          { k: "subalterno", label: "Subalterno", testId: "cat-subalterno" },
        ].map((f) => (
          <div key={f.k}>
            <Label>{f.label} <span className="text-red-700">*</span></Label>
            <Input
              value={data.catasto[f.k]}
              onChange={(e) => setField(`catasto.${f.k}`, e.target.value.replace(/\D+/g, ""))}
              onKeyDown={(e) => {
                // Block non-numeric key presses (letters, symbols) — allow digits,
                // navigation, and common editing shortcuts.
                const allowed = ["Backspace","Delete","ArrowLeft","ArrowRight","ArrowUp","ArrowDown","Tab","Home","End","Enter"];
                if (allowed.includes(e.key)) return;
                if ((e.ctrlKey || e.metaKey) && ["a","c","v","x","z","y"].includes(e.key.toLowerCase())) return;
                if (!/^[0-9]$/.test(e.key)) e.preventDefault();
              }}
              onPaste={(e) => {
                const txt = (e.clipboardData || window.clipboardData).getData("text");
                if (!/^\d+$/.test(txt)) {
                  e.preventDefault();
                  const digits = String(txt).replace(/\D+/g, "");
                  if (digits) setField(`catasto.${f.k}`, digits);
                }
              }}
              inputMode="numeric"
              pattern="[0-9]*"
              className="mt-1.5"
              data-testid={f.testId}
            />
          </div>
        ))}
        <div>
          <Label>MQ appartamento <span className="text-red-700">*</span></Label>
          <Input
            type="number"
            min={1}
            step={0.01}
            placeholder="65"
            value={data.catasto.mq_appartamento || ""}
            onChange={(e) => setField("catasto.mq_appartamento", e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            className="mt-1.5"
            data-testid="cat-mq-appartamento"
          />
          <p className="text-xs text-slate-500 mt-1">Superficie catastale.</p>
        </div>
      </div>

      {/* --- Pertinenze incluse nella STESSA visura dell'appartamento --- */}
      <div className="mt-10 mb-4 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-serif text-lg text-slate-900">
            Pertinenze incluse nella stessa visura{" "}
            <span className="text-sm font-sans font-normal text-slate-500">(facoltative)</span>
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            Balcone, cantina, solaio ecc. che compaiono sulla <strong>stessa visura</strong> dell&apos;appartamento.
            Gli estremi catastali sono ereditati automaticamente; ti serve solo il tipo e i MQ.
            Verranno usati per ricalcolare la superficie totale locata.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            const next = [
              ...pertinenzeIncluse,
              { id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`, tipo: "balconi_terrazzi", mq: "" },
            ];
            setData((prev) => ({ ...prev, catasto: { ...prev.catasto, pertinenze_incluse: next } }));
          }}
          data-testid="pert-incl-add-btn"
        >
          + Aggiungi pertinenza inclusa
        </Button>
      </div>

      {/* Info banner: estremi catastali ereditati dall'appartamento */}
      <div
        className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
        data-testid="pert-incl-catasto-inherited"
      >
        <Info size={16} className="text-slate-500 shrink-0" />
        <span className="text-xs text-slate-500 uppercase tracking-wide">Estremi catastali ereditati dall&apos;appartamento:</span>
        <span className="inline-flex items-center gap-2">
          <span className="text-xs text-slate-500">Foglio</span>
          <span className="font-mono text-sm text-slate-900" data-testid="pert-incl-foglio-inherited">
            {String(data.catasto?.foglio || "").trim() || "—"}
          </span>
        </span>
        <span className="text-slate-300">·</span>
        <span className="inline-flex items-center gap-2">
          <span className="text-xs text-slate-500">Particella</span>
          <span className="font-mono text-sm text-slate-900" data-testid="pert-incl-particella-inherited">
            {String(data.catasto?.particella || "").trim() || "—"}
          </span>
        </span>
        <span className="text-slate-300">·</span>
        <span className="inline-flex items-center gap-2">
          <span className="text-xs text-slate-500">Subalterno</span>
          <span className="font-mono text-sm text-slate-900" data-testid="pert-incl-subalterno-inherited">
            {String(data.catasto?.subalterno || "").trim() || "—"}
          </span>
        </span>
      </div>

      {pertinenzeIncluse.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
          data-testid="pert-incl-empty"
        >
          Nessuna pertinenza inclusa. Aggiungila se sulla stessa visura compaiono anche balcone, cantina o solaio.
        </div>
      ) : (
        <div className="space-y-3" data-testid="pert-incl-list">
          {pertinenzeIncluse.map((p, idx) => (
            <div
              key={p.id}
              className="grid sm:grid-cols-[2fr_1fr_auto] gap-3 items-end border border-slate-200 rounded-md p-4 bg-white"
              data-testid={`pert-incl-row-${idx}`}
            >
              <div>
                <Label>Tipo pertinenza</Label>
                <select
                  value={p.tipo}
                  onChange={(e) => {
                    const next = pertinenzeIncluse.map((x) => (x.id === p.id ? { ...x, tipo: e.target.value } : x));
                    setData((prev) => ({ ...prev, catasto: { ...prev.catasto, pertinenze_incluse: next } }));
                  }}
                  className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid={`pert-incl-tipo-${idx}`}
                >
                  <option value="balconi_terrazzi">Balcone / Terrazzo</option>
                  <option value="cantina">Cantina</option>
                  <option value="soffitta">Soffitta / Solaio</option>
                  <option value="box">Box / Autorimessa</option>
                  <option value="posto_auto">Posto auto</option>
                  <option value="posto_auto_ricarica">Posto auto / Box con ricarica elettrica</option>
                  <option value="superficie_scoperta">Superficie scoperta / Giardino</option>
                </select>
              </div>
              <div>
                <Label>MQ</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={p.mq || ""}
                  onChange={(e) => {
                    const next = pertinenzeIncluse.map((x) => (x.id === p.id ? { ...x, mq: e.target.value } : x));
                    setData((prev) => ({ ...prev, catasto: { ...prev.catasto, pertinenze_incluse: next } }));
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="mt-1.5"
                  data-testid={`pert-incl-mq-${idx}`}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const next = pertinenzeIncluse.filter((x) => x.id !== p.id);
                  setData((prev) => ({ ...prev, catasto: { ...prev.catasto, pertinenze_incluse: next } }));
                }}
                data-testid={`pert-incl-remove-${idx}`}
                className="text-red-700 border-red-200"
              >
                Rimuovi
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 mb-4 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-serif text-lg text-slate-900">Altre pertinenze (visura diversa) <span className="text-sm font-sans font-normal text-slate-500">(facoltative)</span></h3>
          <p className="text-sm text-slate-600 mt-1">
            Aggiungi cantina, box o altre pertinenze <strong>solo se gli estremi catastali sono diversi</strong> da quelli dell&apos;appartamento (visura separata).
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addPertinenza}
          data-testid="pert-add-btn"
        >
          + Aggiungi pertinenza
        </Button>
      </div>

      {pertinenze.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
          data-testid="pert-empty-state"
        >
          Nessuna pertinenza aggiunta. Clicca "+ Aggiungi pertinenza" se cantina/box hanno estremi catastali diversi.
        </div>
      ) : (
        <div className="space-y-4" data-testid="pert-list">
          {pertinenze.map((p, idx) => (
            <div
              key={p.id}
              className="rounded-md border border-slate-200 bg-white p-4"
              data-testid={`pert-item-${idx}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Pertinenza #{idx + 1}</div>
                <button
                  type="button"
                  onClick={() => removePertinenza(p.id)}
                  className="text-xs text-red-700 hover:text-red-800 hover:underline"
                  data-testid={`pert-remove-${idx}`}
                >
                  Rimuovi
                </button>
              </div>
              <div className="grid sm:grid-cols-5 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select
                    value={p.tipo}
                    onValueChange={(v) => updatePertinenza(p.id, "tipo", v)}
                  >
                    <SelectTrigger className="mt-1.5" data-testid={`pert-tipo-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERT_TYPES.map((t) => (
                        <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Foglio</Label>
                  <Input
                    value={p.foglio}
                    onChange={(e) => updatePertinenza(p.id, "foglio", e.target.value.replace(/\D+/g, ""))}
                    onKeyDown={(e) => {
                      if (p.tipo === "balcone" || p.tipo === "terrazzo") return;
                      const allowed = ["Backspace","Delete","ArrowLeft","ArrowRight","ArrowUp","ArrowDown","Tab","Home","End","Enter"];
                      if (allowed.includes(e.key)) return;
                      if ((e.ctrlKey || e.metaKey) && ["a","c","v","x","z","y"].includes(e.key.toLowerCase())) return;
                      if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    readOnly={p.tipo === "balcone" || p.tipo === "terrazzo"}
                    className={`mt-1.5 ${(p.tipo === "balcone" || p.tipo === "terrazzo") ? "bg-slate-100 text-slate-600 cursor-not-allowed" : ""}`}
                    data-testid={`pert-foglio-${idx}`}
                  />
                </div>
                <div>
                  <Label>Particella</Label>
                  <Input
                    value={p.particella}
                    onChange={(e) => updatePertinenza(p.id, "particella", e.target.value.replace(/\D+/g, ""))}
                    onKeyDown={(e) => {
                      if (p.tipo === "balcone" || p.tipo === "terrazzo") return;
                      const allowed = ["Backspace","Delete","ArrowLeft","ArrowRight","ArrowUp","ArrowDown","Tab","Home","End","Enter"];
                      if (allowed.includes(e.key)) return;
                      if ((e.ctrlKey || e.metaKey) && ["a","c","v","x","z","y"].includes(e.key.toLowerCase())) return;
                      if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    readOnly={p.tipo === "balcone" || p.tipo === "terrazzo"}
                    className={`mt-1.5 ${(p.tipo === "balcone" || p.tipo === "terrazzo") ? "bg-slate-100 text-slate-600 cursor-not-allowed" : ""}`}
                    data-testid={`pert-particella-${idx}`}
                  />
                </div>
                <div>
                  <Label>Subalterno</Label>
                  <Input
                    value={p.subalterno}
                    onChange={(e) => updatePertinenza(p.id, "subalterno", e.target.value.replace(/\D+/g, ""))}
                    onKeyDown={(e) => {
                      if (p.tipo === "balcone" || p.tipo === "terrazzo") return;
                      const allowed = ["Backspace","Delete","ArrowLeft","ArrowRight","ArrowUp","ArrowDown","Tab","Home","End","Enter"];
                      if (allowed.includes(e.key)) return;
                      if ((e.ctrlKey || e.metaKey) && ["a","c","v","x","z","y"].includes(e.key.toLowerCase())) return;
                      if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    readOnly={p.tipo === "balcone" || p.tipo === "terrazzo"}
                    className={`mt-1.5 ${(p.tipo === "balcone" || p.tipo === "terrazzo") ? "bg-slate-100 text-slate-600 cursor-not-allowed" : ""}`}
                    data-testid={`pert-subalterno-${idx}`}
                  />
                </div>
                <div>
                  <Label>MQ <span className="text-red-700">*</span> <span className="text-xs font-normal text-slate-400">da visura</span></Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={p.mq || ""}
                    onChange={(e) => updatePertinenza(p.id, "mq", e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="mq"
                    className={`mt-1.5 ${p.mq !== "" && !(Number(p.mq) > 0) ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                    data-testid={`pert-mq-${idx}`}
                  />
                  {p.mq !== "" && !(Number(p.mq) > 0) && (
                    <p className="text-[11px] text-red-700 mt-1" data-testid={`pert-mq-${idx}-error`}>
                      La metratura deve essere maggiore di 0
                    </p>
                  )}
                </div>
              </div>
              {(p.tipo === "balcone" || p.tipo === "terrazzo") && (
                <p
                  className="text-[11px] text-slate-500 mt-2 flex items-center gap-1"
                  data-testid={`pert-balcone-hint-${idx}`}
                >
                  <span className="inline-block w-1 h-1 rounded-full bg-slate-400" />
                  I dati catastali di {p.tipo === "balcone" ? "balcone" : "terrazzo"} corrispondono a quelli dell'appartamento e vengono compilati automaticamente. Puoi modificare solo la metratura.
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------- STEP 6 -------------------

// Duration options per contract type
export const ABITATIVO_OPTIONS = [
  { v: "3", label: "3+2 anni (base 3 anni)", years: 3, magg: "nessuna" },
  { v: "4", label: "4+2 anni (base 4 anni)", years: 4, magg: "4+2" },
  { v: "5", label: "5+2 anni (base 5 anni)", years: 5, magg: "5+2" },
  { v: "6", label: "6+2 anni (base 6 anni)", years: 6, magg: "6+2" },
  { v: "7", label: "7+2 anni o superiore (base 7 anni)", years: 7, magg: "7+2" },
];
// Transitorio: 1..18 months
export const TRANSITORIO_OPTIONS = Array.from({ length: 18 }, (_, i) => ({
  v: String(i + 1),
  label: `${i + 1} ${i === 0 ? "mese" : "mesi"}`,
  months: i + 1,
}));
// Studenti: 6..36 months
export const STUDENTI_OPTIONS = Array.from({ length: 36 - 6 + 1 }, (_, i) => ({
  v: String(i + 6),
  label: `${i + 6} mesi`,
  months: i + 6,
}));

// Compute end date = start + N years/months - 1 day (ISO YYYY-MM-DD)
export function computeContractEndDate(startIso, tipologia, durataValore) {
  if (!startIso || !tipologia || !durataValore) return "";
  const d = new Date(startIso + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  const n = Number(durataValore);
  if (!n || n <= 0) return "";
  if (tipologia === "abitativo") {
    d.setFullYear(d.getFullYear() + n);
  } else {
    // transitorio / studenti → months
    d.setMonth(d.getMonth() + n);
  }
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Compute inclusive duration between two ISO dates as {months, days}, respecting
// calendar months. Convention: data_fine is the LAST day of the lease — a lease
// "dal 15/01 al 14/02" equals exactly "1 mese".
// Returns null if dates are invalid or end < start.
export function computeTransitorioDuration(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const s = new Date(startIso + "T00:00:00");
  const e = new Date(endIso + "T00:00:00");
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return null;
  // Target: the day AFTER the last valid day. Lease length = target - s.
  const targetMs = e.getTime() + 24 * 60 * 60 * 1000;
  let months = 0;
  while (true) {
    const next = new Date(s);
    next.setMonth(next.getMonth() + months + 1);
    if (next.getTime() > targetMs) break;
    months += 1;
    if (months > 240) break; // safety
  }
  const base = new Date(s);
  base.setMonth(base.getMonth() + months);
  const days = Math.round((targetMs - base.getTime()) / (24 * 60 * 60 * 1000));
  return { months, days };
}

// Format a {months, days} duration in Italian: "1 mese e 12 giorni", "18 mesi", "20 giorni"...
export function formatDurationItalian(d) {
  if (!d) return "";
  const { months, days } = d;
  if (!months && !days) return "0 giorni";
  const partMonths = months ? `${months} ${months === 1 ? "mese" : "mesi"}` : "";
  const partDays = days ? `${days} ${days === 1 ? "giorno" : "giorni"}` : "";
  if (partMonths && partDays) return `${partMonths} e ${partDays}`;
  return partMonths || partDays;
}

// Check if a transitorio duration is within the legal 1-month..18-months range.
// Returns {valid, error}.
export function validateTransitorioRange(d) {
  if (!d) return { valid: false, error: "Durata non calcolabile" };
  const { months, days } = d;
  // Minimum: at least 1 month (0 months + any days = not valid)
  if (months < 1) return { valid: false, error: "La durata minima è di 1 mese" };
  // Maximum: strictly ≤ 18 months
  if (months > 18 || (months === 18 && days > 0)) {
    return { valid: false, error: "La durata massima è di 18 mesi" };
  }
  return { valid: true, error: "" };
}

// Derive Step 9 maggiore_durata code from Step 6 tipologia + durata_valore
export function deriveMaggioreDurata(tipologia, durataValore) {
  if (tipologia !== "abitativo") return "nessuna";
  const opt = ABITATIVO_OPTIONS.find((o) => o.v === String(durataValore));
  return opt ? opt.magg : "nessuna";
}

export function Step6Contratto() {
  const { data, setField } = useForm();
  const tip = data.contratto.tipologia;
  const durata = data.contratto.durata_valore;

  const options =
    tip === "abitativo" ? ABITATIVO_OPTIONS :
    tip === "studenti" ? STUDENTI_OPTIONS :
    [];

  const durataLabel =
    tip === "abitativo" ? "Durata base del contratto" :
    tip === "studenti" ? "Durata (6–36 mesi)" :
    "Durata";

  // For transitorio: compute duration from the two user-picked dates
  const transitorioDur = useMemo(
    () => (tip === "transitorio"
      ? computeTransitorioDuration(data.contratto.data_inizio, data.contratto.data_fine)
      : null),
    [tip, data.contratto.data_inizio, data.contratto.data_fine]
  );
  const transitorioLabel = formatDurationItalian(transitorioDur);
  const transitorioCheck = tip === "transitorio" ? validateTransitorioRange(transitorioDur) : null;

  // Auto-compute data_fine ONLY for abitativo/studenti (fixed duration dropdown).
  // For transitorio, data_fine is user-picked and NOT auto-overwritten.
  useEffect(() => {
    if (tip === "transitorio") return;
    const end = computeContractEndDate(data.contratto.data_inizio, tip, durata);
    if (end !== data.contratto.data_fine) {
      setField("contratto.data_fine", end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.contratto.data_inizio, tip, durata]);

  // For transitorio: keep durata_valore / durata_giorni / durata_label in sync
  // with the computed duration between the two dates. Downstream code
  // (contract generator, canone period calc) reads these fields.
  useEffect(() => {
    if (tip !== "transitorio") return;
    const months = transitorioDur?.months ?? "";
    const days = transitorioDur?.days ?? 0;
    const label = transitorioLabel || "";
    if (String(data.contratto.durata_valore ?? "") !== String(months)) {
      setField("contratto.durata_valore", months === "" ? "" : String(months));
    }
    if ((data.contratto.durata_giorni ?? 0) !== days) {
      setField("contratto.durata_giorni", days);
    }
    if ((data.contratto.durata_label ?? "") !== label) {
      setField("contratto.durata_label", label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tip, transitorioDur?.months, transitorioDur?.days, transitorioLabel]);

  // Auto-propagate maggiore_durata to Step 9 whenever tipologia/durata change
  useEffect(() => {
    const magg = deriveMaggioreDurata(tip, durata);
    if (magg !== data.step9.maggiore_durata) {
      setField("step9.maggiore_durata", magg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tip, durata]);

  // When tipologia changes, reset durata_valore (previous value belongs to another unit)
  function onChangeTipologia(v) {
    setField("contratto.tipologia", v);
    setField("contratto.durata_valore", "");
    // Reset transitorio-only fields when leaving transitorio
    if (v !== "transitorio") {
      setField("contratto.durata_giorni", 0);
      setField("contratto.durata_label", "");
    }
    // When entering transitorio, clear data_fine so the user picks it manually
    if (v === "transitorio") {
      setField("contratto.data_fine", "");
    }
  }

  const displayEnd = data.contratto.data_fine;
  const humanEnd = displayEnd ? new Date(displayEnd + "T00:00:00").toLocaleDateString("it-IT") : "";

  return (
    <div className="animate-fade-in-up">
      <SectionHeader overline="Step 6" title="Tipologia Contratto" description="Seleziona la tipologia e la durata: la data di fine viene calcolata automaticamente." />
      <div className="space-y-6 max-w-2xl">
        <div>
          <Label className="mb-3 block">Tipologia di contratto <span className="text-red-700">*</span></Label>
          <RadioGroup
            value={tip}
            onValueChange={onChangeTipologia}
            className="space-y-2"
            data-testid="contratto-tipologia"
          >
            {[
              { v: "abitativo", label: "Abitativo ordinario", helper: "Durate 3+2 / 4+2 / 5+2 / 6+2 / 7+2 anni" },
              { v: "transitorio", label: "Transitorio", helper: "Da 1 a 18 mesi" },
              { v: "studenti", label: "Studenti universitari", helper: "Da 6 a 36 mesi, per studenti fuorisede" },
            ].map((o) => (
              <label
                key={o.v}
                className={`flex items-start gap-3 border rounded-md p-4 cursor-pointer transition-colors ${
                  tip === o.v ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`contratto-tipologia-${o.v}`}
              >
                <RadioGroupItem value={o.v} id={`tipo-${o.v}`} className="mt-0.5" />
                <div>
                  <div className="text-sm text-slate-900 font-medium">{o.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{o.helper}</div>
                </div>
              </label>
            ))}
          </RadioGroup>
        </div>

        {tip && tip !== "transitorio" && (
          <div>
            <Label className="mb-2 block">{durataLabel} <span className="text-red-700">*</span></Label>
            <Select
              value={durata}
              onValueChange={(v) => setField("contratto.durata_valore", v)}
            >
              <SelectTrigger className="max-w-md" data-testid="contratto-durata">
                <SelectValue placeholder="Scegli durata…" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tip === "abitativo" && durata && durata !== "3" && (
              <p className="text-[11px] text-emerald-700 mt-2" data-testid="contratto-magg-hint">
                ⓘ Verrà applicata automaticamente la maggiorazione "Maggiore durata contrattuale" nello Step 9.
              </p>
            )}
          </div>
        )}

        {tip === "transitorio" && (
          <div
            className="border border-amber-300 bg-amber-50 rounded-md p-4 text-sm text-slate-800"
            data-testid="contratto-transitorio-info"
          >
            <p className="font-medium text-amber-900 mb-2">Informativa sulla transitorietà</p>
            <p>
              Ai fini della stipula di un contratto di locazione ad uso transitorio, ai sensi
              dell'art. 5, comma 1, della Legge n. 431/1998 e del D.M. 16/01/2017, è necessario
              allegare idonea documentazione che attesti in modo concreto, specifico e temporaneo
              l'esigenza di transitorietà del locatore e/o del conduttore.
            </p>
            <p className="mt-2">
              La documentazione deve essere coerente con la motivazione indicata nel contratto e
              può consistere, a titolo esemplificativo, in:
            </p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>lettera di trasferimento lavorativo;</li>
              <li>contratto di lavoro a termine;</li>
              <li>lettera di assunzione;</li>
              <li>documentazione medica;</li>
              <li>preliminare di vendita o ristrutturazione dell'immobile;</li>
              <li>dichiarazione del datore di lavoro o altra documentazione ufficiale comprovante l'esigenza temporanea.</li>
            </ul>
            <p className="mt-2 italic text-slate-700">
              La motivazione della transitorietà deve essere chiara, specifica e documentabile;
              non sono sufficienti formule generiche o esigenze future non dimostrate.
            </p>
            <p className="mt-2 text-[12px] text-amber-900">
              ⓘ Nello Step 11 sarà obbligatorio caricare almeno un documento attestante la transitorietà.
            </p>
          </div>
        )}

        {tip === "studenti" && (
          <div
            className="border border-amber-300 bg-amber-50 rounded-md p-4 text-sm text-slate-800"
            data-testid="contratto-studenti-info"
          >
            <p className="font-medium text-amber-900 mb-2">Informativa sulla transitorietà</p>
            <p>
              Ai fini della stipula di un contratto di locazione ad uso transitorio è necessario
              allegare idonea documentazione che attesti in modo concreto, specifico e temporaneo
              l'esigenza di transitorietà del conduttore.
            </p>
            <p className="mt-2">
              La documentazione deve essere coerente con la motivazione indicata nel contratto e
              può consistere, a titolo esemplificativo, in:
            </p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>iscrizione universitaria;</li>
              <li>tesserino universitario.</li>
            </ul>
            <p className="mt-2 italic text-slate-700">
              La motivazione della transitorietà deve essere chiara, specifica e documentabile;
              non sono sufficienti formule generiche o esigenze future non dimostrate.
            </p>
            <p className="mt-2 text-[12px] text-amber-900">
              ⓘ Nello Step 11 sarà obbligatorio caricare almeno un documento attestante l'iscrizione al corso di studi.
            </p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Durata dal <span className="text-red-700">*</span></Label>
            <Input
              type="date"
              value={data.contratto.data_inizio}
              onChange={(e) => setField("contratto.data_inizio", e.target.value)}
              className="mt-1.5"
              data-testid="contratto-data-inizio"
            />
          </div>
          <div>
            <Label>
              al {tip === "transitorio"
                ? <span className="text-red-700">*</span>
                : <span className="text-xs font-normal text-slate-400">(calcolato)</span>}
            </Label>
            <Input
              type="date"
              value={data.contratto.data_fine}
              onChange={tip === "transitorio" ? (e) => setField("contratto.data_fine", e.target.value) : undefined}
              readOnly={tip !== "transitorio"}
              disabled={tip !== "transitorio"}
              className={`mt-1.5 ${tip === "transitorio" ? "" : "bg-slate-50 text-slate-700 cursor-not-allowed"}`}
              data-testid="contratto-data-fine"
            />
            {humanEnd && tip !== "transitorio" && (
              <p className="text-[11px] text-slate-500 mt-1.5" data-testid="contratto-data-fine-human">
                Termine: {humanEnd}
              </p>
            )}
          </div>
        </div>

        {tip === "transitorio" && (
          <div className="rounded-md border p-3" data-testid="contratto-transitorio-durata-box">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Durata calcolata</p>
            {data.contratto.data_inizio && data.contratto.data_fine ? (
              transitorioCheck?.valid ? (
                <p className="text-sm text-emerald-800 font-medium" data-testid="contratto-transitorio-durata-ok">
                  ✓ {transitorioLabel}
                  <span className="ml-2 text-[11px] font-normal text-slate-500">(range consentito: da 1 mese a 18 mesi)</span>
                </p>
              ) : (
                <p className="text-sm text-red-700 font-medium" data-testid="contratto-transitorio-durata-err">
                  ⚠ {transitorioLabel ? `${transitorioLabel} — ` : ""}{transitorioCheck?.error || "Durata non valida"}
                </p>
              )
            ) : (
              <p className="text-sm text-slate-500">Inserisci sia la data di inizio che quella di termine per calcolare la durata.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------- STEP 7 -------------------
export function Step7Valutazione() {
  const { data, setField } = useForm();
  const prevCanRicaricaRef = useRef(false);

  // Compute Q24 eligibility outside of locks so we can also use it in a
  // defaulting effect below.
  const canRicaricaElettrica = useMemo(() => {
    const perts = data.catasto?.pertinenze || [];
    const nonEmpty = (v) => !!String(v || "").trim();
    return perts.some(
      (p) =>
        (p?.tipo === "box" || p?.tipo === "posto_auto" || p?.tipo === "posto_auto_ricarica") &&
        nonEmpty(p?.foglio) &&
        nonEmpty(p?.particella) &&
        nonEmpty(p?.subalterno)
    );
  }, [data.catasto?.pertinenze]);

  // Compute lock state per question (idx 1..25). value is "si" or "no" when locked.
  // Semantics:
  //  - Pertinenza in `pertinenze` (visura SEPARATA / esclusa): if declared, it is
  //    ALWAYS part of the lease → question LOCKED to "sì".
  //  - Pertinenza in `pertinenze_incluse` (stessa visura dell'appartamento): the
  //    owner may or may NOT lease it → question UNLOCKED (default "sì", user can toggle).
  //  - No pertinenza declared → question LOCKED to "no".
  const locks = useMemo(() => {
    const perts = data.catasto?.pertinenze || [];
    const pertsIncluse = data.catasto?.pertinenze_incluse || [];
    const hasEsclusa = (tipos) => perts.some((p) => tipos.includes(p?.tipo));
    const hasInclusa = (tipos) => pertsIncluse.some((p) => tipos.includes(p?.tipo));

    // Q24 unlock condition — vedi `canRicaricaElettrica` calcolato sopra.

    const pianoTerraOrSem =
      data.immobile?.piano === "seminterrato" || data.immobile?.piano === "terra";
    const ascensore = !!data.immobile?.ascensore;

    const r = {};
    // Q2 - Ascensore (unchanged)
    if (pianoTerraOrSem) {
      r[2] = {
        value: "no",
        reason:
          data.immobile?.piano === "seminterrato"
            ? "piano seminterrato — non applicabile"
            : "piano terra — non applicabile",
      };
    } else {
      r[2] = {
        value: ascensore ? "si" : "no",
        reason: `controllato dal flag 'Presenza di ascensore' in Step 4 (${ascensore ? "attivo" : "non attivo"})`,
      };
    }

    // Q6 - Balconi / Terrazzi
    const balconiTipos = ["balcone", "terrazzo", "balconi_terrazzi"];
    if (hasEsclusa(balconiTipos)) {
      r[6] = { value: "si", reason: "Balcone / Terrazzo dichiarato in Step 5 (visura separata — incluso nella locazione)" };
    } else if (!hasInclusa(balconiTipos)) {
      r[6] = { value: "no", reason: "nessun Balcone / Terrazzo in Step 5" };
    }
    // else: pertinenza inclusa nella visura → sbloccato, l'utente sceglie se locarla

    // Q8 - Cantina / Soffitta
    const cantinaTipos = ["cantina", "soffitta"];
    if (hasEsclusa(cantinaTipos)) {
      r[8] = { value: "si", reason: "Cantina / Soffitta dichiarata in Step 5 (visura separata — sempre inclusa nella locazione)" };
    } else if (!hasInclusa(cantinaTipos)) {
      r[8] = { value: "no", reason: "nessuna Cantina / Soffitta in Step 5" };
    }
    // else: pertinenza inclusa → il proprietario può escluderla dalla locazione

    // Q18 - Box / Garage
    if (hasEsclusa(["box"])) {
      r[18] = { value: "si", reason: "Box dichiarato in Step 5 (visura separata — sempre incluso nella locazione)" };
    } else if (!hasInclusa(["box"])) {
      r[18] = { value: "no", reason: "nessun Box in Step 5" };
    }

    // Q19 - Posto auto: SBLOCCATA ogni volta che almeno un Posto auto
    // è stato dichiarato in Step 5 (visura separata o inclusa, tipo
    // "posto_auto" o "posto_auto_ricarica"). Default a "sì", ma il
    // locatore può sempre flaggare "no" liberamente. Resta locked su
    // "no" soltanto quando nessun Posto auto risulta dichiarato.
    if (!hasEsclusa(["posto_auto", "posto_auto_ricarica"]) && !hasInclusa(["posto_auto", "posto_auto_ricarica"])) {
      r[19] = { value: "no", reason: "nessun Posto auto in Step 5" };
    }

    // Q24 - Ricarica elettrica: bloccata su "no" SOLO quando non esiste alcun
    // Box/Posto auto in Step 5 con visura separata e dati catastali completi.
    // Quando esiste (anche se la pertinenza è di tipo "posto_auto_ricarica"
    // esplicito), la domanda resta SBLOCCATA: viene impostata di default a "sì"
    // (vedi effect più sotto), ma il locatore può comunque flaggare "no".
    if (!canRicaricaElettrica) {
      r[24] = {
        value: "no",
        reason:
          "richiede Box o Posto auto con visura separata e dati catastali completi (foglio, particella, subalterno) in Step 5",
      };
    }
    return r;
  }, [data.catasto, data.immobile]);

  // Sync locked values into state so the score reflects them
  useEffect(() => {
    Object.entries(locks).forEach(([idx, lock]) => {
      const key = `q${idx}`;
      if (data.valutazione[key] !== lock.value) {
        setField(`valutazione.${key}`, lock.value);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locks]);

  // Q24 default: when the "ricarica elettrica" eligibility flips from false
  // to true (i.e. the user has just added a Box/Posto auto with catastals
  // in Step 5), default Q24 to "sì". The question stays UNLOCKED so the
  // locatore can still toggle it back to "no" afterwards.
  useEffect(() => {
    if (canRicaricaElettrica && !prevCanRicaricaRef.current) {
      if (data.valutazione.q24 !== "si") {
        setField("valutazione.q24", "si");
      }
    }
    prevCanRicaricaRef.current = canRicaricaElettrica;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRicaricaElettrica]);
  // NOTE: Seeding of default "sì" for pertinenze INCLUSE is handled centrally
  // in FormContext (see the pertinenze → valutazione effect). It preserves any
  // explicit user choice made in Step 7.

  const score = EVALUATION_QUESTIONS.reduce((acc, _, i) => {
    const idx = i + 1;
    return acc + (data.valutazione[`q${idx}`] === "si" ? (QUESTION_WEIGHTS[idx] || 0) : 0);
  }, 0);
  const subFascia = score <= 13 ? 1 : score <= 21 ? 2 : 3;

  return (
    <div className="animate-fade-in-up">
      <SectionHeader
        overline="Step 7"
        title="Valutazione"
        description="L'immobile oggetto di locazione presenta le seguenti caratteristiche, ai fini di quanto previsto all'art. 1, co. 4, D.M. 16/01/2017."
      />

      <InfoBox testId="valutazione-info">
        Ogni criterio vale <strong>1, 2 o 3 punti</strong> in base alla rilevanza. Il punteggio
        ottenuto determina la <strong>sub-fascia</strong> di riferimento.
      </InfoBox>

      <div className="space-y-2 mt-6" data-testid="valutazione-list">
        {EVALUATION_QUESTIONS.map((q, i) => {
          const idx = i + 1;
          const key = `q${idx}`;
          const val = data.valutazione[key] || "no";
          const weight = QUESTION_WEIGHTS[idx] || 0;
          const lock = locks[idx];
          const isLocked = !!lock;
          const lockValue = lock?.value;
          const lockReason = lock?.reason || "";
          return (
            <div
              key={key}
              className="flex items-start gap-4 border border-slate-200 rounded-md p-4 bg-white"
              data-testid={`valutazione-${idx}`}
            >
              <span className="text-xs font-mono text-slate-400 shrink-0 pt-1 w-6">{idx}.</span>
              <div className="flex-1 text-sm text-slate-800 leading-relaxed">
                {q}
                <span
                  className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums bg-slate-100 text-slate-600 align-middle"
                  data-testid={`valutazione-${idx}-weight`}
                  title={`Vale ${weight} punto${weight === 1 ? "" : "i"} se Sì`}
                >
                  {weight} pt
                </span>
                {isLocked && (
                  <div
                    className="mt-1.5 text-[11px] text-slate-500 italic"
                    data-testid={`valutazione-${idx}-locked-hint`}
                  >
                    {lockValue === "si"
                      ? `Bloccato su Sì: ${lockReason}.`
                      : `Bloccato su No: ${lockReason}.`}
                  </div>
                )}
                {(idx === 9 || idx === 10) && !isLocked && (
                  <div
                    className="mt-1.5 text-[11px] text-slate-500 italic"
                    data-testid={`valutazione-${idx}-mutex-hint`}
                  >
                    ⓘ Escluso a vicenda con {idx === 9 ? "il Riscaldamento centralizzato (n. 10)" : "il Riscaldamento autonomo (n. 9)"}. Selezionandone uno, l'altro passa automaticamente a No.
                  </div>
                )}
                {([6, 8, 18, 19].includes(idx)) && !isLocked && (
                  <div
                    className="mt-1.5 text-[11px] text-slate-500 italic"
                    data-testid={`valutazione-${idx}-incluse-hint`}
                  >
                    ⓘ Pertinenza inclusa nella visura dell&apos;appartamento (Step 5). Puoi indicare <strong>No</strong> se il proprietario ha scelto di non includerla nella locazione.
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {["si", "no"].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      if (isLocked) return;
                      setField(`valutazione.${key}`, opt);
                      // Mutual exclusion Q9 (autonomo) vs Q10 (centralizzato)
                      if (opt === "si") {
                        if (idx === 9) setField("valutazione.q10", "no");
                        else if (idx === 10) setField("valutazione.q9", "no");
                      }
                    }}
                    disabled={isLocked}
                    className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors min-w-[48px] ${
                      isLocked
                        ? val === opt
                          ? "bg-slate-200 text-slate-500 border-slate-200 cursor-not-allowed"
                          : "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"
                        : val === opt
                        ? opt === "si"
                          ? "bg-green-800 text-white border-green-800"
                          : "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                    }`}
                    data-testid={`valutazione-${idx}-${opt}`}
                  >
                    {opt === "si" ? "Sì" : "No"}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-0 bg-white border border-slate-200 rounded-lg p-5 mt-8 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="overline mb-1">Punteggio totale</div>
            <div className="text-3xl font-serif text-slate-900" data-testid="valutazione-score">
              {score} / {MAX_SCORE}
            </div>
          </div>
          <div>
            <div className="overline mb-1">Sub-fascia</div>
            <div className="text-3xl font-serif text-green-800" data-testid="valutazione-subfascia">
              {subFascia}
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-4 leading-relaxed">
          Sub-fascia 1 ≤ 13 punti · Sub-fascia 2 = 14–21 punti · Sub-fascia 3 ≥ 22 punti
        </p>
      </div>
    </div>
  );
}

// ------------------- STEP 8 -------------------
export function Step8Superfici() {
  const { data, setField } = useForm();

  const num = (k) => Number(data.superfici[k]) || 0;

  // ---- Sync pertinenze mq (Step 5) → superfici (Step 8) on mount ----
  const perts = data.catasto?.pertinenze || [];
  const pertsIncluse = data.catasto?.pertinenze_incluse || [];
  const mqAppartamento = Number(data.catasto?.mq_appartamento) || 0;

  // Whether the owner is leasing only a portion of the apartment (Requirement 1a).
  const isPorzione = !!data.superfici.locazione_porzione;

  // ---- Camere da letto (richiesto SOLO in locazione parziale) ----
  // L'utente aggiunge TUTTE le camere da letto dell'appartamento (anche non
  // locate). Esattamente una camera può essere flaggata "locata" → quella
  // oggetto del contratto. Il numero di stanze (`in_stanze`) e la superficie
  // degli spazi comuni (`spazi_comuni`) vengono calcolati automaticamente.
  const camere = Array.isArray(data.superfici.camere) ? data.superfici.camere : [];
  const genId = () => Math.random().toString(36).slice(2, 10);
  const addCamera = () => {
    const next = [...camere, { id: genId(), nome: `Camera ${camere.length + 1}`, mq: "", locata: false }];
    setField("superfici.camere", next);
  };
  const updateCamera = (id, patch) => {
    const next = camere.map((c) => (c.id === id ? { ...c, ...patch } : c));
    setField("superfici.camere", next);
  };
  const setCameraLocata = (id) => {
    const next = camere.map((c) => ({ ...c, locata: c.id === id }));
    setField("superfici.camere", next);
  };
  const removeCamera = (id) => {
    const next = camere.filter((c) => c.id !== id);
    setField("superfici.camere", next);
  };
  const sumCamereMq = useMemo(
    () => camere.reduce((acc, c) => acc + (Number(c.mq) || 0), 0),
    [camere]
  );
  const cameraLocata = useMemo(() => camere.find((c) => c.locata) || null, [camere]);

  // ---- Filter INCLUSE pertinenze by Step 7 answer (Requirement 2): if the
  // owner declared the pertinenza in the apartment's visura but marked it as
  // NOT leased in Step 7, remove its mq from the calculation. Pertinenze in
  // separate visura (`perts`) are ALWAYS leased and cannot be removed.
  const val = data.valutazione || {};
  const isIncludedInLease = (tipo) => {
    if (tipo === "cantina" || tipo === "soffitta") return val.q8 !== "no";
    if (tipo === "balcone" || tipo === "terrazzo" || tipo === "balconi_terrazzi") return val.q6 !== "no";
    if (tipo === "box") return val.q18 !== "no";
    if (tipo === "posto_auto" || tipo === "posto_auto_ricarica") return val.q19 !== "no";
    return true;
  };
  const pertsInclusaLocate = pertsIncluse.filter((p) => isIncludedInLease(p?.tipo));

  // Coefficients per pertinenza type — MUST match the row coefficients below.
  const COEF_BY_TIPO = {
    box: 0.5,
    posto_auto: 0.25,
    posto_auto_ricarica: 0.25,
    balconi_terrazzi: 0.3,
    balcone: 0.3,
    terrazzo: 0.3,
    cantina: 0.25,
    soffitta: 0.25,
    superficie_scoperta: 0.1,
  };

  // Superficie "netta" dell'appartamento (mq da visura - sum pertinenze
  // inclusa locate × coefficiente). Usata:
  //   - come `supTotAuto` nel caso di locazione piena
  //   - come base di calcolo per gli `spazi_comuni` in locazione parziale
  const appNettoBase = useMemo(() => {
    if (!mqAppartamento) return 0;
    let subtract = 0;
    pertsInclusaLocate.forEach((p) => {
      const mq = Number(p.mq) || 0;
      const coef = COEF_BY_TIPO[p.tipo] ?? 0;
      subtract += mq * coef;
    });
    return Math.max(0, Math.round((mqAppartamento - subtract) * 100) / 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mqAppartamento, JSON.stringify(pertsInclusaLocate)]);

  // Superficie totale locata (auto-calcolata quando esiste `mq_appartamento`):
  //   = mq_appartamento - Σ(mq_pertinenza_inclusa_locata × coef)
  // When the owner leases only a portion, the total is NOT auto-locked —
  // they enter it manually (must be < mqAppartamento).
  const supTotAuto = useMemo(() => {
    if (!mqAppartamento) return null;
    if (isPorzione) return null;
    return appNettoBase;
  }, [mqAppartamento, appNettoBase, isPorzione]);

  // When the auto value differs from the stored one, sync it into data.superfici.
  useEffect(() => {
    if (supTotAuto !== null && String(supTotAuto) !== String(data.superfici.superficie_totale || "")) {
      setField("superfici.superficie_totale", String(supTotAuto));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supTotAuto]);

  // When switching FROM porzione back to full unit, clear the manual value so the
  // auto-calc effect above can seed the freshly-computed value.
  useEffect(() => {
    if (!isPorzione && mqAppartamento > 0 && supTotAuto !== null) {
      // handled by the effect above
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPorzione]);

  // Reset `in_stanze`, `spazi_comuni` e `camere` quando l'utente esce dalla
  // modalità "porzione" (così i campi non restano popolati con valori residui).
  useEffect(() => {
    if (!isPorzione) {
      if (data.superfici.in_stanze !== "") setField("superfici.in_stanze", "");
      if (data.superfici.spazi_comuni !== "") setField("superfici.spazi_comuni", "");
      if ((data.superfici.camere || []).length > 0) setField("superfici.camere", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPorzione]);

  // Auto-sync camere → in_stanze + spazi_comuni + superficie_totale (porzione)
  //
  // Importante: `spazi_comuni` input conterrà la *quota per camera* (già divisa
  // per il numero di stanze) — non il totale da visura. Il totale visura è
  // comunque mostrato nel riepilogo "Breakdown" sopra la tabella.
  //   quota_per_camera = (appNettoBase − somma_camere) / num_camere
  const spaziComuniAuto = useMemo(() => {
    if (!isPorzione) return null;
    if (camere.length < 1) return null;
    const base = appNettoBase;
    if (!base) return null;
    const totale = base - sumCamereMq;
    const divisor = camere.length || 1;
    const v = totale / divisor;
    return Math.max(0, Math.round(v * 100) / 100);
  }, [isPorzione, camere.length, appNettoBase, sumCamereMq]);

  // Totale "da visura" degli spazi comuni (NON diviso) — usato per il breakdown
  const spaziComuniTotale = useMemo(() => {
    if (!isPorzione || camere.length < 1 || !appNettoBase) return null;
    return Math.max(0, Math.round((appNettoBase - sumCamereMq) * 100) / 100);
  }, [isPorzione, camere.length, appNettoBase, sumCamereMq]);

  useEffect(() => {
    if (!isPorzione) return;
    const nStanze = String(camere.length || "");
    if (nStanze !== (data.superfici.in_stanze || "")) {
      setField("superfici.in_stanze", nStanze);
    }
    if (spaziComuniAuto !== null && String(spaziComuniAuto) !== String(data.superfici.spazi_comuni || "")) {
      setField("superfici.spazi_comuni", String(spaziComuniAuto));
    }
    // Superficie della porzione locata = mq camera locata + quota spazi comuni
    // per camera (= `spaziComuniAuto`). Calcolo automatico solo quando esiste
    // una camera flaggata "locata".
    if (cameraLocata && camere.length >= 2 && spaziComuniAuto !== null) {
      const mqLocata = Number(cameraLocata.mq) || 0;
      const supTotPorz = Math.round((mqLocata + spaziComuniAuto) * 100) / 100;
      if (String(supTotPorz) !== String(data.superfici.superficie_totale || "")) {
        setField("superfici.superficie_totale", String(supTotPorz));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPorzione, camere.length, spaziComuniAuto, cameraLocata?.id, cameraLocata?.mq]);

  // ---- Step 8 "Locare SI/NO" toggle per row (Requirement: pertinenze
  //   INCLUSE possono essere escluse dalla locazione; pertinenze in visura
  //   SEPARATA sono sempre locate). The toggle writes back to the Step 7
  //   question that governs the lease flag.
  //   Keys: box_garage→q18, posto_auto→q19, balconi_terrazzi→q6, cantina→q8.
  const Q7_KEY_BY_ROW = { box_garage: "q18", posto_auto: "q19", balconi_terrazzi: "q6", cantina: "q8" };
  const TIPO_BY_ROW = {
    box_garage: ["box"],
    posto_auto: ["posto_auto", "posto_auto_ricarica"],
    balconi_terrazzi: ["balcone", "terrazzo", "balconi_terrazzi"],
    cantina: ["cantina", "soffitta"],
  };
  const toggleStateFor = (rowKey) => {
    const tipos = TIPO_BY_ROW[rowKey];
    if (!tipos) return null;
    const hasEsc = perts.some((p) => tipos.includes(p?.tipo));
    const hasInc = pertsIncluse.some((p) => tipos.includes(p?.tipo));
    if (!hasEsc && !hasInc) return null; // nessuna pertinenza → non mostriamo il toggle
    const q = Q7_KEY_BY_ROW[rowKey];
    const current = val?.[q] === "no" ? "no" : "si"; // default SI
    return {
      q7Key: q,
      current,
      lockedSi: hasEsc, // visura separata ⇒ forziamo SI, "NO" disabilitato
      hasInclusa: hasInc,
    };
  };

  // ---- Pertinenze presence (from Step 5) drives 5 surface fields (locked/readonly) ----
  // Only leased incluse are counted; ALL separate visura pertinenze are counted.
  const pertsHas = useMemo(() => {
    const combined = [...perts, ...pertsInclusaLocate];
    return {
      box: combined.some((p) => p.tipo === "box"),
      posto_auto: combined.some((p) => p.tipo === "posto_auto" || p.tipo === "posto_auto_ricarica"),
      cantina: combined.some((p) => p.tipo === "cantina" || p.tipo === "soffitta"),
      balconi_terrazzi: combined.some((p) => p.tipo === "balcone" || p.tipo === "terrazzo" || p.tipo === "balconi_terrazzi"),
      superficie_scoperta: combined.some((p) => p.tipo === "superficie_scoperta"),
    };
  }, [perts, pertsInclusaLocate]);
  // Which types have an inclusa but NOT-leased pertinenza (for the info hint).
  const pertsHasNonLeasedInclusa = useMemo(() => {
    const nonLeased = pertsIncluse.filter((p) => !isIncludedInLease(p?.tipo));
    return {
      box: nonLeased.some((p) => p.tipo === "box"),
      posto_auto: nonLeased.some((p) => p.tipo === "posto_auto" || p.tipo === "posto_auto_ricarica"),
      cantina: nonLeased.some((p) => p.tipo === "cantina" || p.tipo === "soffitta"),
      balconi_terrazzi: nonLeased.some((p) => p.tipo === "balcone" || p.tipo === "terrazzo" || p.tipo === "balconi_terrazzi"),
      superficie_scoperta: false,
    };
  }, [pertsIncluse, val.q6, val.q8, val.q18, val.q19]);
  const pertsSum = useMemo(() => {
    const s = { box: 0, posto_auto: 0, cantina: 0, balconi_terrazzi: 0, superficie_scoperta: 0 };
    [...perts, ...pertsInclusaLocate].forEach((p) => {
      const mq = Number(p.mq) || 0;
      if (p.tipo === "box") s.box += mq;
      else if (p.tipo === "posto_auto" || p.tipo === "posto_auto_ricarica") s.posto_auto += mq;
      else if (p.tipo === "cantina" || p.tipo === "soffitta") s.cantina += mq;
      else if (p.tipo === "balcone" || p.tipo === "terrazzo" || p.tipo === "balconi_terrazzi") s.balconi_terrazzi += mq;
      else if (p.tipo === "superficie_scoperta") s.superficie_scoperta += mq;
    });
    return {
      box: Math.round(s.box * 100) / 100,
      posto_auto: Math.round(s.posto_auto * 100) / 100,
      cantina: Math.round(s.cantina * 100) / 100,
      balconi_terrazzi: Math.round(s.balconi_terrazzi * 100) / 100,
      superficie_scoperta: Math.round(s.superficie_scoperta * 100) / 100,
    };
  }, [perts, pertsInclusaLocate]);

  // Auto-sync pertsSum → data.superfici for locked rows.
  // In locazione parziale (stanze >= 2), each pertinenza is either "comune" (mq
  // diviso per numero di stanze) o "esclusivo" (metratura intera). Requirement 1c.
  const stanzeForSplit = Number(data.superfici.in_stanze) || 0;
  const isParzialeSplit = stanzeForSplit >= 2;
  const modeFor = (k) => data.superfici.esclusivo_mode?.[k] || "comune";
  const computeEff = (total, k) => {
    const t = Math.max(0, Number(total) || 0);
    if (!isParzialeSplit) return Math.round(t * 100) / 100;
    if (modeFor(k) === "esclusivo") return Math.round(t * 100) / 100;
    // comune (default): divisa per il numero di stanze
    return Math.round((t / stanzeForSplit) * 100) / 100;
  };
  useEffect(() => {
    const mapping = {
      box_garage: computeEff(pertsSum.box, "box_garage"),
      posto_auto: computeEff(pertsSum.posto_auto, "posto_auto"),
      balconi_terrazzi: computeEff(pertsSum.balconi_terrazzi, "balconi_terrazzi"),
      cantina: computeEff(pertsSum.cantina, "cantina"),
      superficie_scoperta: computeEff(pertsSum.superficie_scoperta, "superficie_scoperta"),
    };
    Object.entries(mapping).forEach(([k, v]) => {
      if (String(v) !== String(data.superfici[k] || "")) {
        setField(`superfici.${k}`, String(v));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pertsSum,
    isParzialeSplit,
    stanzeForSplit,
    data.superfici.esclusivo_mode?.box_garage,
    data.superfici.esclusivo_mode?.posto_auto,
    data.superfici.esclusivo_mode?.balconi_terrazzi,
    data.superfici.esclusivo_mode?.cantina,
    data.superfici.esclusivo_mode?.superficie_scoperta,
  ]);

  // Compute recalculated mq per row
  const supTot = num("superficie_totale");
  const spazi = num("spazi_comuni");
  const stanze = num("in_stanze");
  // Quando in locazione parziale, `spazi_comuni` contiene già la *quota per
  // camera* (vedi `spaziComuniAuto`), quindi ricalcolato = valore identità.
  // Negli altri casi (eventuali flussi legacy) manteniamo la divisione.
  const spaziRicalc = (isPorzione && camere.length >= 2)
    ? spazi
    : (stanze >= 2 ? Math.round((spazi / stanze) * 100) / 100 : 0);

  // Validation flags
  const isLocazioneParziale = spazi > 0 || stanze > 0;
  const stanzeError = spazi > 0 && stanze <= 0
    ? "Devi specificare il numero di stanze (minimo 2) per procedere con la locazione parziale."
    : stanze === 1
      ? "Il numero minimo di stanze per una locazione parziale è 2."
      : "";
  const supTotError = supTot > 0 && supTot < 9
    ? "La superficie totale / porzione di appartamento non può essere inferiore a 9 mq."
    : isPorzione && supTot > 0 && mqAppartamento > 0 && supTot >= mqAppartamento
      ? `La porzione di appartamento (${supTot} mq) deve essere inferiore alla superficie totale della visura (${mqAppartamento} mq).`
      : "";
  // Requirement 1b: spazi comuni sempre inferiori alla superficie totale
  const spaziError = spazi > 0 && supTot > 0 && spazi >= supTot
    ? `Gli spazi comuni (${spazi} mq) devono essere inferiori alla superficie totale (${supTot} mq).`
    : "";

  const rows = [
    { k: "superficie_totale", label: isPorzione ? "Superficie della porzione locata" : "Superficie Totale / Porzione di appartamento", coefLabel: "Coefficiente: 1.0", recalc: Math.round(supTot * 1.0 * 100) / 100, autoLocked: (!isPorzione && supTotAuto !== null) || (isPorzione && !!cameraLocata && camere.length >= 2) },
    ...(isPorzione ? [
      {
        k: "spazi_comuni",
        label: "Superficie spazi comuni (quota per camera)",
        coefLabel: `Quota per camera = (appartamento netto − somma camere) ÷ ${camere.length || 1}`,
        recalc: spaziRicalc,
        autoLocked: spaziComuniAuto !== null,
      },
      {
        k: "in_stanze",
        label: "In stanze (solo per locazione parziale)",
        coefLabel: "Numero stanze · usato come divisore per gli spazi comuni",
        recalc: null,
        autoLocked: camere.length > 0,
      },
    ] : []),
    { k: "box_garage", label: "Box / Garage", coefLabel: "Coefficiente: 0.5", recalc: Math.round(num("box_garage") * 0.5 * 100) / 100, pertSum: pertsSum.box, pertLabel: "Box", pertLocked: true, pertHas: pertsHas.box },
    { k: "posto_auto", label: "Posto auto all'aperto esclusivo", coefLabel: "Coefficiente: 0.25", recalc: Math.round(num("posto_auto") * 0.25 * 100) / 100, pertSum: pertsSum.posto_auto, pertLabel: "Posto auto", pertLocked: true, pertHas: pertsHas.posto_auto },
    { k: "balconi_terrazzi", label: "Balconi / Terrazzi", coefLabel: "Coefficiente: 0.3", recalc: Math.round(num("balconi_terrazzi") * 0.3 * 100) / 100, pertSum: pertsSum.balconi_terrazzi, pertLabel: "Balcone/Terrazzo", pertLocked: true, pertHas: pertsHas.balconi_terrazzi },
    { k: "cantina", label: "Cantina o affine", coefLabel: "Coefficiente: 0.25", recalc: Math.round(num("cantina") * 0.25 * 100) / 100, pertSum: pertsSum.cantina, pertLabel: "Cantina/Soffitta", pertLocked: true, pertHas: pertsHas.cantina },
    { k: "superficie_scoperta", label: "Superficie scoperta in godimento esclusivo", coefLabel: "Coefficiente: 0.1", recalc: Math.round(num("superficie_scoperta") * 0.1 * 100) / 100, pertSum: pertsSum.superficie_scoperta, pertLabel: "Superficie scoperta / Giardino", pertLocked: true, pertHas: pertsHas.superficie_scoperta },
  ];

  // Total before maggiorazione = sum of all rows where recalc !== null
  let totalBefore = 0;
  rows.forEach((r) => {
    if (r.recalc !== null) totalBefore += r.recalc;
  });
  totalBefore = Math.round(totalBefore * 100) / 100;

  // Auto-apply the ≤45 mq maggiorazione:
  // - Locazione parziale (stanze>0 o spazi_comuni>0): SEMPRE OFF
  // - Se pertinenze con catasto diverso (visura separata) esistono E mq_appartamento < 45: SEMPRE ON
  // - Altrimenti: ON se totalBefore <= 45 e > 0
  const hasSeparatePertinenze = perts.length > 0;
  const smallApartment = mqAppartamento > 0 && mqAppartamento < 45;
  let canApplyMagg;
  if (isLocazioneParziale) {
    canApplyMagg = false;
  } else if (hasSeparatePertinenze && smallApartment) {
    canApplyMagg = true;
  } else {
    canApplyMagg = totalBefore <= 45 && totalBefore > 0;
  }
  useEffect(() => {
    if (!!data.superfici.maggiorazione_45mq !== canApplyMagg) {
      setField("superfici.maggiorazione_45mq", canApplyMagg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApplyMagg]);
  const maggApplied = canApplyMagg;
  const maggValue = maggApplied ? Math.round(totalBefore * 0.10 * 100) / 100 : 0;
  const totalFinal = Math.round((totalBefore + maggValue) * 100) / 100;

  return (
    <div className="animate-fade-in-up">
      <SectionHeader
        overline="Step 8"
        title="Tipologia Superficie e Ricalcolo MQ"
        description="Inserisci i MQ da visura. Il ricalcolo avverrà in automatico."
      />

      {/* Requirement 1a: checkbox "affitta una porzione dell'appartamento" */}
      {mqAppartamento > 0 && (
        <label
          className="flex items-start gap-3 mb-5 p-3 border border-slate-200 rounded-md bg-slate-50 cursor-pointer"
          data-testid="sup-porzione-wrap"
        >
          <Checkbox
            checked={isPorzione}
            onCheckedChange={(v) => setField("superfici.locazione_porzione", !!v)}
            className="mt-0.5"
            data-testid="sup-porzione-check"
          />
          <div className="text-sm">
            <div className="font-medium text-slate-900">Affitto solo una porzione dell&apos;appartamento</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Se attivo, aggiungi tutte le camere da letto dell&apos;appartamento (anche quelle NON locate) qui sotto: numero stanze e spazi comuni verranno calcolati automaticamente.
            </p>
          </div>
        </label>
      )}

      {/* Elenco camere da letto — SOLO in locazione parziale */}
      {isPorzione && (
        <div className="mb-6 p-4 border border-slate-200 rounded-md bg-white" data-testid="camere-section">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <h3 className="font-serif text-slate-900 text-base">Camere da letto</h3>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed max-w-xl">
                Aggiungi <strong>tutte</strong> le camere da letto, con la relativa metratura da visura.
                Flagga <strong>una sola camera</strong> come &quot;locata&quot; (quella oggetto del contratto).
                Minimo 2 stanze. Ogni camera deve essere di almeno 9 mq.
              </p>
            </div>
            <button
              type="button"
              onClick={addCamera}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-black"
              data-testid="camere-add-btn"
            >
              + Aggiungi camera
            </button>
          </div>

          {camere.length === 0 ? (
            <p className="text-xs text-slate-500 italic p-3 border border-dashed border-slate-300 rounded">
              Nessuna camera aggiunta. Clicca &quot;Aggiungi camera&quot; per inserire ogni camera da letto.
            </p>
          ) : (
            <div className="space-y-2">
              {camere.map((c, i) => {
                const mqNum = Number(c.mq) || 0;
                const tooSmall = mqNum > 0 && mqNum < 9;
                return (
                  <div
                    key={c.id}
                    className={`grid sm:grid-cols-12 gap-2 items-center p-2.5 rounded border ${
                      c.locata ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-slate-50"
                    }`}
                    data-testid={`camera-row-${i}`}
                  >
                    <div className="sm:col-span-4">
                      <Input
                        type="text"
                        value={c.nome}
                        onChange={(e) => updateCamera(c.id, { nome: e.target.value })}
                        placeholder={`Camera ${i + 1}`}
                        className="text-sm"
                        data-testid={`camera-nome-${i}`}
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={c.mq}
                        onChange={(e) => updateCamera(c.id, { mq: e.target.value })}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="mq da visura"
                        className={`text-sm tabular-nums ${tooSmall ? "border-red-500" : ""}`}
                        data-testid={`camera-mq-${i}`}
                      />
                    </div>
                    <div className="sm:col-span-4">
                      <label className="flex items-center gap-2 text-xs text-slate-800 cursor-pointer">
                        <input
                          type="radio"
                          name="camera-locata"
                          checked={!!c.locata}
                          onChange={() => setCameraLocata(c.id)}
                          data-testid={`camera-locata-${i}`}
                        />
                        <span className={c.locata ? "font-semibold text-emerald-800" : "text-slate-700"}>
                          {c.locata ? "✓ Camera locata" : "Flagga come camera locata"}
                        </span>
                      </label>
                    </div>
                    <div className="sm:col-span-1 text-right">
                      <button
                        type="button"
                        onClick={() => removeCamera(c.id)}
                        className="text-slate-400 hover:text-red-700 text-base leading-none px-2 py-1"
                        aria-label="Rimuovi camera"
                        data-testid={`camera-remove-${i}`}
                      >
                        ×
                      </button>
                    </div>
                    {tooSmall && (
                      <div className="sm:col-span-12 text-[11px] text-red-700">
                        La metratura minima per una camera da letto è 9 mq.
                      </div>
                    )}
                  </div>
                );
              })}

              {camere.length >= 1 && camere.length < 2 && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="camere-warning-min">
                  Per la locazione parziale sono richieste <strong>almeno 2 camere da letto</strong>.
                </p>
              )}
              {camere.length >= 2 && !cameraLocata && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="camere-warning-nolocata">
                  Flagga esattamente <strong>una</strong> camera come &quot;Camera locata&quot;.
                </p>
              )}

              {/* Breakdown automatico */}
              {camere.length >= 1 && appNettoBase > 0 && (
                <div className="mt-3 p-3 bg-slate-900 text-white rounded text-xs space-y-1 leading-relaxed" data-testid="camere-breakdown">
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-300">MQ appartamento (visura):</span>
                    <span className="font-mono">{mqAppartamento} mq</span>
                  </div>
                  {pertsInclusaLocate.length > 0 && (
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-300">− Pertinenze incluse ricalcolate:</span>
                      <span className="font-mono">{Math.round((mqAppartamento - appNettoBase) * 100) / 100} mq</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2 border-t border-white/20 pt-1">
                    <span className="text-slate-300">Appartamento escluse pertinenze:</span>
                    <span className="font-mono">{appNettoBase} mq</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-300">− Somma camere da letto ({camere.length}):</span>
                    <span className="font-mono">{Math.round(sumCamereMq * 100) / 100} mq</span>
                  </div>
                  <div className="flex justify-between gap-2 border-t border-white/20 pt-1 font-semibold">
                    <span>= Spazi comuni (visura):</span>
                    <span className="font-mono">{spaziComuniTotale !== null ? spaziComuniTotale : 0} mq</span>
                  </div>
                  {camere.length >= 2 && spaziComuniAuto !== null && (
                    <div className="flex justify-between gap-2 text-emerald-300 font-semibold">
                      <span>Quota spazi comuni per camera (÷{camere.length}):</span>
                      <span className="font-mono">{spaziComuniAuto} mq</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.k} className="grid sm:grid-cols-12 gap-3 items-start border-b border-slate-100 pb-3">
            <div className="sm:col-span-6">
              <Label className="text-slate-700">{r.label}</Label>
              <p className="text-xs text-slate-500 mt-0.5">{r.coefLabel}</p>
              {r.pertLocked && r.pertHas && (
                <p
                  className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1.5 inline-block"
                  data-testid={`sup-${r.k}-pertsum`}
                >
                  ⓘ Auto-compilato da Step 5 · {r.pertSum} mq ({r.pertLabel})
                </p>
              )}
              {r.pertLocked && r.pertHas && isParzialeSplit && (
                <div
                  className="mt-2 flex items-center gap-2 text-[11px] text-slate-700"
                  data-testid={`sup-${r.k}-mode-wrap`}
                >
                  <span className="text-[11px] text-slate-700 whitespace-nowrap">Uso:</span>
                  {["comune", "esclusivo"].map((opt) => {
                    const active = modeFor(r.k) === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setField(`superfici.esclusivo_mode.${r.k}`, opt)}
                        className={`text-[11px] font-medium px-2.5 py-1 rounded border transition-colors ${
                          active
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                        }`}
                        data-testid={`sup-${r.k}-mode-${opt}`}
                      >
                        {opt === "comune" ? "Comune" : "Esclusivo"}
                      </button>
                    );
                  })}
                  <span className="text-[11px] text-slate-500">
                    {modeFor(r.k) === "comune"
                      ? `(mq totali ÷ ${stanzeForSplit} stanze)`
                      : "(intera metratura mantenuta)"}
                  </span>
                </div>
              )}
              {r.pertLocked && !r.pertHas && pertsHasNonLeasedInclusa[r.k === "box_garage" ? "box" : r.k] && (
                <p
                  className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5"
                  data-testid={`sup-${r.k}-nonleased`}
                >
                  🚫 Pertinenza inclusa in visura ma esclusa dalla locazione. Metratura esclusa dal calcolo.
                </p>
              )}

              {/* Locare SI/NO toggle (Step 8 shortcut for Step 7 answers) */}
              {(() => {
                const t = toggleStateFor(r.k);
                if (!t) return null;
                return (
                  <div className="mt-2 flex items-center gap-2" data-testid={`sup-${r.k}-locare-wrap`}>
                    <span className="text-[11px] text-slate-700 font-medium whitespace-nowrap">Locare:</span>
                    {[
                      { v: "si", label: "SÌ" },
                      { v: "no", label: "NO" },
                    ].map((opt) => {
                      const active = t.current === opt.v;
                      // "NO" bloccato quando esiste visura separata (sempre inclusa)
                      const disabled = opt.v === "no" && t.lockedSi;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          disabled={disabled}
                          onClick={() => !disabled && setField(`valutazione.${t.q7Key}`, opt.v)}
                          className={`text-[11px] font-medium px-2.5 py-1 rounded border transition-colors ${
                            active
                              ? "bg-slate-900 text-white border-slate-900"
                              : disabled
                                ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                                : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                          }`}
                          data-testid={`sup-${r.k}-locare-${opt.v}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                    {t.lockedSi && (
                      <span className="text-[11px] text-slate-500 italic">
                        pertinenza in visura separata — sempre locata
                      </span>
                    )}
                    {!t.lockedSi && t.hasInclusa && (
                      <span className="text-[11px] text-slate-500 italic">
                        inclusa nella visura: puoi escluderla dalla locazione
                      </span>
                    )}
                  </div>
                );
              })()}
              {r.pertLocked && !r.pertHas && (
                <p
                  className="text-[11px] text-slate-500 italic mt-1.5"
                  data-testid={`sup-${r.k}-nopert`}
                >
                  Bloccato a 0 mq: nessuna pertinenza di tipo "{r.pertLabel}" segnalata nello Step 5.
                </p>
              )}
            </div>
            <div className="sm:col-span-3">
              <Input
                type="number"
                inputMode="decimal"
                step={r.k === "in_stanze" ? "1" : "0.01"}
                min={r.k === "in_stanze" ? "2" : "0"}
                value={data.superfici[r.k]}
                onChange={(e) => !r.pertLocked && !r.autoLocked && setField(`superfici.${r.k}`, e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                readOnly={!!r.pertLocked || !!r.autoLocked}
                disabled={!!r.pertLocked || !!r.autoLocked}
                placeholder={r.k === "in_stanze" ? "min. 2" : "mq"}
                data-testid={`sup-${r.k}`}
                className={`${r.pertLocked || r.autoLocked ? "bg-slate-50 text-slate-700 cursor-not-allowed" : ""} ${
                  (r.k === "in_stanze" && stanzeError) ||
                  (r.k === "superficie_totale" && supTotError) ||
                  (r.k === "spazi_comuni" && spaziError)
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : ""
                }`}
              />
              {r.autoLocked && (
                <p className="text-[10px] text-slate-500 mt-1 italic" data-testid={`sup-${r.k}-auto-hint`}>
                  {r.k === "in_stanze"
                    ? "Calcolato automaticamente dalle camere da letto aggiunte sopra."
                    : r.k === "spazi_comuni"
                      ? `Quota per camera = (MQ appartamento − pertinenze ricalcolate − somma camere) ÷ ${camere.length || 1}.`
                      : r.k === "superficie_totale" && isPorzione
                        ? "Calcolata automaticamente: mq camera locata + quota spazi comuni (÷ n° stanze)."
                        : "Calcolata automaticamente da Step 5 (MQ appartamento − pertinenze incluse ricalcolate)."}
                </p>
              )}
              {r.k === "in_stanze" && stanzeError && (
                <p className="mt-1.5 text-[11px] text-red-700" data-testid="sup-in-stanze-error">
                  {stanzeError}
                </p>
              )}
              {r.k === "superficie_totale" && supTotError && (
                <p className="mt-1.5 text-[11px] text-red-700" data-testid="sup-superficie-totale-error">
                  {supTotError}
                </p>
              )}
              {r.k === "spazi_comuni" && spaziError && (
                <p className="mt-1.5 text-[11px] text-red-700" data-testid="sup-spazi-comuni-error">
                  {spaziError}
                </p>
              )}
            </div>
            <div className="sm:col-span-3">
              {r.recalc !== null ? (
                <div className="text-sm text-slate-900 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 tabular-nums">
                  <span className="text-xs text-slate-500 mr-2">ricalcolati:</span>
                  <span className="font-medium" data-testid={`sup-${r.k}-ricalc`}>{r.recalc} mq</span>
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic px-3 py-2">
                  non incluso nel totale
                </div>
              )}
            </div>
            {r.k === "spazi_comuni" && spazi > 0 && (
              <div
                className="sm:col-span-12 mt-1 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 leading-relaxed"
                data-testid="sup-spazi-comuni-alert"
              >
                Per procedere con la locazione parziale, l&apos;immobile deve disporre di <strong>almeno due stanze da letto private</strong>. Ciascuna camera destinata a una sola persona deve inoltre rispettare la metratura minima obbligatoria di <strong>9 metri quadri</strong>.
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 p-5 border border-slate-200 rounded-lg bg-white">
        <label className="flex items-start gap-3 cursor-not-allowed">
          <Checkbox
            checked={maggApplied}
            disabled
            data-testid="sup-magg-45"
            className="mt-1"
          />
          <div>
            <div className="font-medium text-slate-900">Maggiorazione per alloggi ≤ 45 mq (+10%)</div>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="sup-magg-45-hint">
              {isLocazioneParziale
                ? "Non applicabile: locazione parziale — la maggiorazione ≤45 mq non si applica alle porzioni di appartamento."
                : hasSeparatePertinenze && smallApartment && !(totalBefore <= 45 && totalBefore > 0)
                  ? `Applicata: pertinenze con visura separata e appartamento < 45 mq (${mqAppartamento} mq).`
                  : maggApplied
                    ? `Applicata automaticamente: totale ricalcolato ${totalBefore} mq ≤ 45.`
                    : totalBefore > 45
                      ? `Non applicabile: totale ricalcolato ${totalBefore} mq > 45.`
                      : "In attesa che venga inserita la superficie totale."}
            </p>
          </div>
        </label>

        <div className="grid sm:grid-cols-3 gap-4 mt-5 pt-4 border-t border-slate-100">
          <div>
            <div className="overline mb-1">Totale MQ prima maggiorazione</div>
            <div className="text-xl font-serif text-slate-900 tabular-nums" data-testid="sup-total-before">
              {totalBefore} mq
            </div>
          </div>
          <div>
            <div className="overline mb-1">Maggiorazione 10%</div>
            <div className="text-xl font-serif text-slate-900 tabular-nums" data-testid="sup-total-magg">
              {maggValue} mq
            </div>
          </div>
          <div>
            <div className="overline mb-1">Totale MQ finali</div>
            <div className="text-xl font-serif text-green-800 tabular-nums" data-testid="sup-total-final">
              {totalFinal} mq
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------- STEP 9 -------------------
export function Step9Maggiorazioni() {
  const { data, setField } = useForm();
  const energiaPercent = (c) => (c === "A" ? 10 : c === "G" ? -2 : 0);
  const durataPercent = (d) => ({ "4+2": 3, "5+2": 5, "6+2": 8, "7+2": 10 }[d] || 0);

  const amm = data.step9.ammobiliato === "si" ? 15 : 0;
  const en = energiaPercent(data.step9.classe_energetica);
  const cs = data.step9.cantina_soffitta === "no" ? -2 : 0;
  const sem = data.step9.seminterrato === "si" ? -10 : 0;
  const dur = durataPercent(data.step9.maggiore_durata);
  const total = amm + en + cs + sem + dur;

  return (
    <div className="animate-fade-in-up">
      <SectionHeader overline="Step 9" title="Maggiorazioni / Riduzioni" />

      <div className="space-y-6">
        <YesNoField
          label="Alloggio ammobiliato"
          helper="Se Sì: maggiorazione fino al +15%"
          value={data.step9.ammobiliato}
          onChange={(v) => setField("step9.ammobiliato", v)}
          testId="step9-ammobiliato"
        />

        <div>
          <Label className="mb-2 block">Classe Energetica</Label>
          <Select
            value={data.step9.classe_energetica}
            onValueChange={(v) => setField("step9.classe_energetica", v)}
          >
            <SelectTrigger className="max-w-xs" data-testid="step9-classe">
              <SelectValue placeholder="Seleziona classe" />
            </SelectTrigger>
            <SelectContent>
              {["A", "B", "C", "D", "E", "F", "G"].map((c) => (
                <SelectItem key={c} value={c}>
                  Classe {c}
                  {c === "A" && " (+10%)"}
                  {c === "G" && " (-2%)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1.5">A: +10% · G: -2% · altre: 0%</p>
        </div>

        <YesNoField
          label="Cantina / Soffitta"
          helper="Se NO: riduzione -2% sui valori massimi"
          value={data.step9.cantina_soffitta}
          onChange={(v) => setField("step9.cantina_soffitta", v)}
          testId="step9-cantina"
          locked
          lockedHint={
            (data.catasto?.pertinenze || []).some((p) => p.tipo === "cantina" || p.tipo === "soffitta")
              ? "Bloccato su Sì: pertinenza Cantina/Soffitta segnalata in Step 5."
              : "Bloccato su No: nessuna Cantina/Soffitta segnalata in Step 5 (applicato -2%)."
          }
        />

        <YesNoField
          label="Alloggio situato al piano seminterrato"
          helper="Se Sì: riduzione -10% sui valori massimi"
          value={data.step9.seminterrato}
          onChange={(v) => setField("step9.seminterrato", v)}
          testId="step9-seminterrato"
          locked
          lockedHint={
            data.immobile?.piano === "seminterrato"
              ? "Bloccato su Sì: piano seminterrato selezionato in Step 4 (applicato -10%)."
              : "Bloccato su No: piano non seminterrato (impostato in Step 4)."
          }
        />

        <div className="max-w-md rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="step9-canone-info">
          <p>
            Il <strong>canone mensile richiesto</strong> viene indicato nel prossimo step ("Risultato Calcolo") una volta calcolato il range minimo-massimo. Il valore inserito lì sarà usato sia nell'attestazione bilaterale sia nella generazione del contratto (escluse le spese condominiali).
          </p>
        </div>

        <div>
          <Label className="mb-2 block">Maggiore durata contrattuale</Label>
          <Select
            value={data.step9.maggiore_durata}
            onValueChange={(v) => setField("step9.maggiore_durata", v)}
            disabled={data.contratto.tipologia !== "" && data.contratto.tipologia !== "abitativo" || (data.contratto.tipologia === "abitativo" && !!data.contratto.durata_valore)}
          >
            <SelectTrigger
              className="max-w-md"
              data-testid="step9-durata"
              disabled={data.contratto.tipologia !== "" && data.contratto.tipologia !== "abitativo" || (data.contratto.tipologia === "abitativo" && !!data.contratto.durata_valore)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nessuna">Nessuna</SelectItem>
              <SelectItem value="4+2">Durata 4+2 (+3%)</SelectItem>
              <SelectItem value="5+2">Durata 5+2 (+5%)</SelectItem>
              <SelectItem value="6+2">Durata 6+2 (+8%)</SelectItem>
              <SelectItem value="7+2">Durata 7+2 o superiore (+10%)</SelectItem>
            </SelectContent>
          </Select>
          {data.contratto.tipologia === "abitativo" && !!data.contratto.durata_valore && (
            <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1.5 inline-block" data-testid="step9-durata-autolink">
              ⓘ Impostato automaticamente dal Step 6 (Tipologia Contratto). Per modificare, torna al Step 6.
            </p>
          )}
          {(data.contratto.tipologia === "transitorio" || data.contratto.tipologia === "studenti") && (
            <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1 mt-1.5 inline-block" data-testid="step9-durata-notabitativo">
              Bloccato su &quot;Nessuna&quot;: i contratti {data.contratto.tipologia === "transitorio" ? "transitori" : "per studenti universitari"} hanno durate standard senza maggiorazione per durata contrattuale.
            </p>
          )}
        </div>

        <div className="border border-slate-200 rounded-lg p-5 bg-slate-50">
          <div className="overline mb-2">Somma % Maggiorazioni / Riduzioni</div>
          <div className={`text-3xl font-serif tabular-nums ${total >= 0 ? "text-green-800" : "text-red-700"}`} data-testid="step9-total">
            {total > 0 ? "+" : ""}{total}%
          </div>
        </div>
      </div>
    </div>
  );
}

function YesNoField({ label, helper, value, onChange, testId, locked = false, lockedHint }) {
  return (
    <div>
      <Label className="mb-2 block">{label}</Label>
      <div className="flex gap-2">
        {["si", "no"].map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => !locked && onChange(o)}
            disabled={locked}
            className={`text-sm font-medium px-4 py-2 rounded-md border transition-colors ${
              locked
                ? value === o
                  ? "bg-slate-200 text-slate-500 border-slate-200 cursor-not-allowed"
                  : "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"
                : value === o
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
            }`}
            data-testid={`${testId}-${o}`}
          >
            {o === "si" ? "Sì" : "No"}
          </button>
        ))}
      </div>
      {locked && lockedHint && (
        <p className="text-[11px] text-slate-500 italic mt-1.5" data-testid={`${testId}-locked-hint`}>
          {lockedHint}
        </p>
      )}
      {helper && <p className="text-xs text-slate-500 mt-1.5">{helper}</p>}
    </div>
  );
}

// ------------------- STEP 10 -------------------
export function Step10Documents() {
  const { submissionId, files, setFiles, data, setField } = useForm();
  const byDocType = Object.fromEntries((files || []).map((f) => [f.doc_type, f]));
  const tipologia = data.contratto?.tipologia;
  const [apeModalOpen, setApeModalOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);

  // Filter: keep base docs + only the conditional doc that matches contract type
  const baseDocs = DOCUMENT_TYPES.filter((d) => {
    if (!d.requiredWhen) return true;
    return d.requiredWhen === tipologia;
  });

  // Requirement 1 — per ogni pertinenza con visura SEPARATA e dati catastali
  // completi (foglio + particella + subalterno) aggiungi 2 righe dinamiche
  // per la visura e la planimetria della pertinenza stessa.
  const separatePertinenze = (data.catasto?.pertinenze || []).filter(
    (p) =>
      String(p?.foglio || "").trim() &&
      String(p?.particella || "").trim() &&
      String(p?.subalterno || "").trim()
  );
  const TIPO_LABELS = {
    box: "Box", posto_auto: "Posto auto", cantina: "Cantina", soffitta: "Soffitta",
    balcone: "Balcone", terrazzo: "Terrazzo", superficie_scoperta: "Sup. scoperta",
  };
  const pertinenzaDocs = separatePertinenze.flatMap((p, i) => {
    const tag = TIPO_LABELS[p.tipo] || p.tipo || `Pertinenza ${i + 1}`;
    return [
      {
        key: `visura_pertinenza_${p.id || i}`,
        label: `Visura catastale — ${tag} (F. ${p.foglio} · P. ${p.particella} · S. ${p.subalterno})`,
        pertinenzaDoc: true,
      },
      {
        key: `planimetria_pertinenza_${p.id || i}`,
        label: `Planimetria catastale — ${tag} (F. ${p.foglio} · P. ${p.particella} · S. ${p.subalterno})`,
        pertinenzaDoc: true,
      },
    ];
  });

  // Insert pertinenzaDocs subito dopo la planimetria catastale (indice fisso base)
  const insertAt = baseDocs.findIndex((d) => d.key === "planimetria_catastale") + 1;
  const visibleDocs = [
    ...baseDocs.slice(0, insertAt),
    ...pertinenzaDocs,
    ...baseDocs.slice(insertAt),
  ];

  // Requirement 3 — sindacato scelto per il bonifico all'associazione inquilini
  const sindacatoKey = data.associazione_inquilini || "uniat";
  const sindacato = SINDACATI_INQUILINI.find((s) => s.key === sindacatoKey) || SINDACATI_INQUILINI[0];

  // APE expiry logic
  const apeExpiry = data.ape_scadenza || "";
  const today = new Date().toISOString().slice(0, 10);
  const apeIsExpired = apeExpiry && apeExpiry < today;

  return (
    <div className="animate-fade-in-up">
      <SectionHeader
        overline="Step 11"
        title="Carica i Documenti dell'Immobile"
        description="Solo file PDF — max 15 MB per file. Puoi sostituire o eliminare ogni documento prima dell'invio."
      />
      {!submissionId && (
        <InfoBox testId="upload-needs-id">
          Compila almeno i dati del locatore nello step 2 per abilitare il caricamento dei
          documenti.
        </InfoBox>
      )}

      {/* Scheda Iscrizione Unioncasa — download-first, prominent CTA */}
      <div
        className="mt-6 flex flex-wrap items-center gap-3 rounded-md border border-slate-300 bg-slate-50 px-4 py-3"
        data-testid="scheda-iscrizione-cta"
      >
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-medium text-slate-900">Scheda di Iscrizione Unioncasa</p>
          <p className="text-xs text-slate-600 mt-0.5">
            Scarica il modulo vuoto in PDF, compilalo e caricalo tra i documenti richiesti qui sotto.
          </p>
        </div>
        <a
          href={`${API}/public/scheda-iscrizione.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-3 py-2 text-xs font-medium hover:bg-slate-800 transition-colors"
          data-testid="scheda-iscrizione-download-top"
        >
          <Download size={14} />
          Scarica scheda vuota
        </a>
      </div>

      {tipologia === "transitorio" && (
        <div
          className="mt-6 border border-amber-300 bg-amber-50 rounded-md p-4 text-sm text-slate-800"
          data-testid="doc-transitorio-info"
        >
          <p className="font-medium text-amber-900 mb-2">Documentazione obbligatoria per la transitorietà</p>
          <p>
            Ai fini della stipula di un contratto di locazione ad uso transitorio, ai sensi
            dell'art. 5, comma 1, della Legge n. 431/1998 e del D.M. 16/01/2017, è necessario
            allegare idonea documentazione che attesti in modo concreto, specifico e temporaneo
            l'esigenza di transitorietà del locatore e/o del conduttore.
          </p>
        </div>
      )}

      {tipologia === "studenti" && (
        <div
          className="mt-6 border border-sky-300 bg-sky-50 rounded-md p-4 text-sm text-slate-800"
          data-testid="doc-studenti-info"
        >
          <p className="font-medium text-sky-900 mb-1">Documentazione universitaria richiesta</p>
          <p>
            Per il contratto ad uso "Studenti universitari" è obbligatorio allegare la
            <strong> lettera di iscrizione all'università</strong> oppure il
            <strong> tesserino universitario</strong> del conduttore.
          </p>
        </div>
      )}

      <div className="mt-6 border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="documents-table">
        <div className="hidden md:grid grid-cols-[48px_minmax(0,1fr)_minmax(0,340px)] items-center gap-4 bg-slate-50 border-b border-slate-200 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <div>#</div>
          <div>Documento</div>
          <div className="text-right pr-2">File caricato</div>
        </div>
        {visibleDocs.map((d, idx) => {
          const rec = byDocType[d.key];
          const isContract = d.key === "contratto_locazione";
          const isVisura = d.key === "visura_catastale";
          const isApe = d.askExpiry;
          const isSchedaIscr = d.key === "scheda_iscrizione";
          const isBonificoUnioncasa = d.key === "ricevuta_bonifico";
          const isBonificoSindacato = d.key === "ricevuta_bonifico_sindacato";
          const hasError = !rec && !isContract; // pending upload
          return (
            <div
              key={d.key}
              className={`grid grid-cols-[48px_minmax(0,1fr)_minmax(0,340px)] gap-4 items-start px-4 py-4 border-b border-slate-100 last:border-b-0 ${
                rec ? "bg-emerald-50/30" : "bg-white"
              }`}
              data-testid={`doc-row-${d.key}`}
            >
              {/* Numero */}
              <div className="pt-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                    rec
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                      : "bg-slate-100 text-slate-600 border border-slate-200"
                  }`}
                >
                  {rec ? "✓" : idx + 1}
                </div>
              </div>

              {/* Info documento */}
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 leading-tight">
                  {d.label} <span className="text-red-700">*</span>
                </p>
                {d.note && (
                  <p className="text-[11px] text-amber-800 mt-1 leading-snug" data-testid={`upload-${d.key}-note`}>
                    {d.note}
                  </p>
                )}
                {isContract && (
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    Generato esclusivamente tramite il tool integrato — nessun upload manuale.
                  </p>
                )}

                {/* Extras: visura mq checkbox */}
                {isVisura && rec && (
                  <label
                    className="mt-2.5 flex items-start gap-2 p-2 border border-slate-200 rounded bg-white cursor-pointer"
                    data-testid="visura-mq-confirm-wrap"
                  >
                    <Checkbox
                      checked={!!data.visura_mq_ok}
                      onCheckedChange={(v) => setField("visura_mq_ok", !!v)}
                      data-testid="visura-mq-confirm"
                      className="mt-0.5"
                    />
                    <span className="text-[11px] text-slate-700 leading-snug">
                      Confermo che la visura riporta l&apos;indicazione dei <strong>mq</strong>. Senza questo dato la pratica non può proseguire.
                    </span>
                  </label>
                )}

                {/* Extras: scheda iscrizione download */}
                {isSchedaIscr && (
                  <a
                    href={`${API}/public/scheda-iscrizione.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-[11px] text-slate-700 hover:text-slate-900 underline underline-offset-2"
                    data-testid="scheda-iscrizione-download-inline"
                  >
                    <Download size={11} />
                    Scarica il modulo vuoto da compilare (PDF)
                  </a>
                )}

                {/* Requirement 2 — coordinate IBAN Unioncasa per il bonifico */}
                {isBonificoUnioncasa && (
                  <div
                    className="mt-2.5 border border-slate-200 rounded bg-slate-50 p-2.5 text-[11px] text-slate-700"
                    data-testid="unioncasa-iban-info"
                  >
                    <p className="font-semibold text-slate-900 mb-1">Coordinate bancarie Unioncasa</p>
                    <div className="grid grid-cols-1 gap-0.5">
                      <div><span className="text-slate-500">Intestatario:</span> <strong>{UNIONCASA_IBAN.intestatario}</strong></div>
                      <div><span className="text-slate-500">IBAN:</span> <code className="text-slate-900">{UNIONCASA_IBAN.iban}</code></div>
                      <div><span className="text-slate-500">Banca:</span> <strong>{UNIONCASA_IBAN.banca}</strong></div>
                    </div>
                  </div>
                )}

                {/* Requirement 3 — selettore sindacato inquilini + coordinate */}
                {isBonificoSindacato && (
                  <div
                    className="mt-2.5 border border-slate-200 rounded bg-slate-50 p-2.5 text-[11px] text-slate-700"
                    data-testid="sindacato-inquilini-info"
                  >
                    <Label className="text-[11px] font-medium text-slate-800 mb-1 block">
                      Sindacato inquilini aderente all&apos;accordo <span className="text-red-700">*</span>
                    </Label>
                    <select
                      className="h-8 w-full max-w-xs rounded border border-slate-300 px-2 text-xs bg-white"
                      value={sindacatoKey}
                      onChange={(e) => setField("associazione_inquilini", e.target.value)}
                      data-testid="sindacato-inquilini-select"
                    >
                      {SINDACATI_INQUILINI.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>

                    {(sindacato.iban || sindacato.intestatario) ? (
                      <div className="mt-2 grid grid-cols-1 gap-0.5">
                        {sindacato.intestatario && (
                          <div><span className="text-slate-500">Intestatario:</span> <strong>{sindacato.intestatario}</strong></div>
                        )}
                        {sindacato.iban && (
                          <div><span className="text-slate-500">IBAN:</span> <code className="text-slate-900">{sindacato.iban}</code></div>
                        )}
                        {sindacato.banca && (
                          <div><span className="text-slate-500">Banca:</span> {sindacato.banca}</div>
                        )}
                        {sindacato.importo !== null && sindacato.importo !== undefined && (
                          <div><span className="text-slate-500">Importo:</span> <strong>€ {Number(sindacato.importo).toFixed(2)}</strong></div>
                        )}
                        {sindacato.causale && (
                          <div><span className="text-slate-500">Causale:</span> {sindacato.causale}</div>
                        )}
                      </div>
                    ) : (
                      <p className="mt-2 italic text-slate-500">
                        Coordinate bancarie da richiedere direttamente al sindacato — contattaci se non le hai ricevute.
                      </p>
                    )}
                  </div>
                )}

                {/* Extras: contratto generator callout */}
                {isContract && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setContractModalOpen(true)}
                      disabled={!submissionId}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="open-contract-generator-btn"
                    >
                      {rec?.generated ? "Rigenera contratto PDF" : "Genera contratto PDF →"}
                    </button>
                    {!rec && (
                      <span className="text-[11px] text-red-700">Nessun contratto ancora generato.</span>
                    )}
                    {rec?.generated && (
                      <span className="text-[11px] text-emerald-800">✓ Generato automaticamente da Unioncasa.</span>
                    )}
                  </div>
                )}

                {/* Extras: APE expiry */}
                {isApe && rec && (
                  <div className="mt-2.5 p-2.5 border border-slate-200 rounded bg-white" data-testid="ape-expiry-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Label className="text-[11px] font-medium text-slate-700 mb-0">Data scadenza APE <span className="text-red-700">*</span></Label>
                      <Input
                        type="date"
                        value={apeExpiry}
                        onChange={(e) => setField("ape_scadenza", e.target.value)}
                        className="h-8 text-xs w-[160px]"
                        data-testid="ape-scadenza"
                        max="2099-12-31"
                      />
                    </div>
                    {apeIsExpired && (
                      <div className="mt-2 border border-red-300 bg-red-50 rounded p-2.5 text-[11px] text-red-900" data-testid="ape-expired-warning">
                        <p className="font-medium">⚠ APE scaduto ({apeExpiry})</p>
                        <p className="mt-1">Non è possibile proseguire. Unioncasa offre il servizio di redazione a <strong>€ 120,00</strong> per gli associati.</p>
                        <div className="mt-2 rounded border border-red-200 bg-white/70 p-2" data-testid="ape-required-docs">
                          <p className="font-medium mb-1">Documentazione richiesta per la nuova APE:</p>
                          <ul className="list-disc pl-4 space-y-0.5 leading-snug">
                            <li>Documento del proprietario + codice fiscale</li>
                            <li>Dati catastali (foglio, mappale, subalterno)</li>
                            <li>Planimetria catastale (se in possesso)</li>
                            <li>Libretto caldaia + prova fumi (max 2 anni)</li>
                            <li>Epoca installazione caldaia e costruzione immobile</li>
                            <li>Tipo serramenti (vetro doppio/singolo) + epoca installazione</li>
                            <li>Produzione acqua calda sanitaria (boiler elettrico/gas)</li>
                            <li>Presenza split A/C + epoca installazione</li>
                          </ul>
                        </div>
                        <button
                          type="button"
                          onClick={() => setApeModalOpen(true)}
                          className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded bg-red-800 text-white text-[11px] font-medium hover:bg-red-900"
                          data-testid="ape-request-service-btn"
                        >
                          Richiedi il servizio APE →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Colonna Upload */}
              <div className="w-full">
                {isContract ? (
                  rec ? (
                    <div className="flex items-center justify-between gap-3 border border-emerald-300 bg-emerald-50 rounded-md px-3 py-2" data-testid={`upload-${d.key}-filled`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <FileCheck2 size={16} className="text-emerald-700 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-slate-900 truncate">{rec.filename}</div>
                          <div className="text-[11px] text-slate-500">{(rec.size / 1024).toFixed(1)} KB · generato</div>
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-emerald-700">auto</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center border border-dashed border-slate-300 bg-slate-50 rounded-md px-3 py-2.5 text-[11px] text-slate-500" data-testid={`upload-${d.key}-empty`}>
                      Usa &quot;Genera contratto PDF&quot; a sinistra
                    </div>
                  )
                ) : (
                  <FileUploadField
                    docType={d.key}
                    label={d.label}
                    submissionId={submissionId}
                    fileRecord={rec}
                    otherFiles={files || []}
                    note={d.note}
                    compact
                    hideLabel
                    onChange={(newRec) => {
                      const others = (files || []).filter((f) => f.doc_type !== d.key);
                      setFiles(newRec ? [...others, newRec] : others);
                    }}
                    required
                    testId={`upload-${d.key}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ApeServiceRequestModal
        open={apeModalOpen}
        onOpenChange={setApeModalOpen}
        prefill={{
          nome: data.locatore?.nome || "",
          email: data.locatore?.email || "",
          indirizzo_immobile: `${data.immobile?.via || ""} ${data.immobile?.civico || ""}, ${data.immobile?.cap || ""} ${data.immobile?.citta || ""}`.trim(),
        }}
        submissionId={submissionId}
      />

      <ContractGeneratorModal
        open={contractModalOpen}
        onOpenChange={setContractModalOpen}
        submissionId={submissionId}
        data={data}
        onGenerated={(rec) => {
          const others = (files || []).filter((f) => f.doc_type !== "contratto_locazione");
          setFiles([...others, rec]);
        }}
      />
    </div>
  );
}

function ApeServiceRequestModal({ open, onOpenChange, prefill, submissionId }) {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    indirizzo_immobile: "",
    codice_fiscale: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        nome: prefill?.nome || f.nome,
        email: prefill?.email || f.email,
        indirizzo_immobile: prefill?.indirizzo_immobile || f.indirizzo_immobile,
      }));
      setDone(false);
    }
  }, [open, prefill]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nome.trim() || !form.email.trim() || !form.telefono.trim()) {
      toast.error("Compila nome, email e telefono");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/ape-service-request", { ...form, submission_id: submissionId });
      setDone(true);
      toast.success("Richiesta inviata correttamente");
    } catch (e) {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ape-service-modal">
        <DialogHeader>
          <DialogTitle>Richiedi il servizio APE — € 120,00</DialogTitle>
          <DialogDescription>
            Compila i tuoi dati: Unioncasa Milano ti ricontatterà per organizzare la produzione della certificazione APE.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-2xl mb-3">✓</div>
            <p className="font-medium text-slate-900">Richiesta ricevuta</p>
            <p className="text-sm text-slate-500 mt-1">Ti contatteremo al più presto all'indirizzo indicato.</p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-4 px-4 py-2 rounded-md bg-slate-900 text-white text-sm"
              data-testid="ape-modal-close-done"
            >
              Chiudi
            </button>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div>
              <Label>Nome e cognome <span className="text-red-700">*</span></Label>
              <Input value={form.nome} onChange={(e) => update("nome", e.target.value)} data-testid="ape-form-nome" className="mt-1.5" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Email <span className="text-red-700">*</span></Label>
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="ape-form-email" className="mt-1.5" />
              </div>
              <div>
                <Label>Telefono <span className="text-red-700">*</span></Label>
                <Input value={form.telefono} onChange={(e) => update("telefono", e.target.value)} data-testid="ape-form-telefono" className="mt-1.5" placeholder="+39 …" />
              </div>
            </div>
            <div>
              <Label>Indirizzo immobile</Label>
              <Input value={form.indirizzo_immobile} onChange={(e) => update("indirizzo_immobile", e.target.value)} data-testid="ape-form-indirizzo" className="mt-1.5" />
            </div>
            <div>
              <Label>Codice fiscale / n. associato <span className="text-xs text-slate-400 font-normal">(facoltativo)</span></Label>
              <Input value={form.codice_fiscale} onChange={(e) => update("codice_fiscale", e.target.value)} data-testid="ape-form-cf" className="mt-1.5" />
            </div>
            <div>
              <Label>Note <span className="text-xs text-slate-400 font-normal">(facoltativo)</span></Label>
              <Textarea rows={3} value={form.note} onChange={(e) => update("note", e.target.value)} data-testid="ape-form-note" className="mt-1.5" placeholder="Es. preferenze orarie, urgenza, ecc." />
            </div>
            <DialogFooter className="pt-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 bg-white hover:bg-slate-50"
                data-testid="ape-form-cancel"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 rounded-md bg-red-800 text-white text-sm font-medium hover:bg-red-900 disabled:opacity-60"
                data-testid="ape-form-submit"
              >
                {submitting ? "Invio…" : "Invia richiesta"}
              </button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ------------------- STEP 11 -------------------
export function Step11Result({ calculation, loadingCalc }) {
  const { data, setField } = useForm();

  return (
    <div className="animate-fade-in-up">
      <SectionHeader
        overline="Step 10"
        title="Risultato calcolo Canone Concordato Milano"
        description="Sulla base dei dati inseriti, il risultato è:"
      />

      {loadingCalc ? (
        <div className="py-16 text-center text-slate-500">
          <Calculator size={32} className="mx-auto mb-2 animate-pulse" />
          Calcolo in corso...
        </div>
      ) : !calculation ? (
        <InfoBox testId="no-calc">
          Non è stato possibile calcolare il canone. Verifica zona, superfici e valutazione.
        </InfoBox>
      ) : (
        <div className="space-y-6">
          {/* Top row: 4 summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ResultTile label="Zona" value={`Zona ${calculation.zona}`} testId="r-zona" />
            <ResultTile label="Sub-fascia" value={calculation.sub_fascia} testId="r-subfascia" />
            <ResultTile label="MQ riparametrati" value={`${calculation.mq_riparametrati} mq`} testId="r-mq" />
            <ResultTile
              label="Variazione"
              value={`${calculation.variazione_percentuale > 0 ? "+" : ""}${calculation.variazione_percentuale}%`}
              testId="r-var"
            />
          </div>

          {/* Bottom row: 50/50 BIG canone min / max */}
          <div className="grid sm:grid-cols-2 gap-4">
            <CanoneCard
              variant="min"
              label="Canone minimo mensile"
              value={formatEuro(calculation.canone_minimo_mensile)}
              helper="mq riparametrati × €/mq minimo"
              testId="r-min"
            />
            <CanoneCard
              variant="max"
              label="Canone massimo mensile"
              value={formatEuro(calculation.canone_massimo_mensile)}
              helper={`mq × €/mq max ${calculation.variazione_percentuale !== 0 ? `· variazione ${calculation.variazione_percentuale > 0 ? "+" : ""}${calculation.variazione_percentuale}%` : ""}`}
              testId="r-max"
            />
          </div>

          <InfoBox testId="r-info">
            Valori basati sull'Accordo territoriale per il Comune di Milano. <br />
            <strong>Canone minimo mensile</strong> = mq riparametrati × €/mq min. <br />
            <strong>Canone massimo mensile</strong> = mq riparametrati × €/mq max, aumentato o
            ridotto della percentuale di maggiorazioni/riduzioni.
          </InfoBox>

          {/* Canone richiesto dall'associato */}
          {(() => {
            const canoneReq = Number(data.step9?.canone_convenuto) || 0;
            const min = Number(calculation.canone_minimo_mensile) || 0;
            const max = Number(calculation.canone_massimo_mensile) || 0;
            const isOverMax = canoneReq > 0 && max > 0 && canoneReq > max;
            const isBelowMin = canoneReq > 0 && min > 0 && canoneReq < min;
            const isCongruo = canoneReq > 0 && !isOverMax;
            return (
              <div
                className={`border rounded-lg p-5 bg-white ${
                  isOverMax ? "border-red-400" : isCongruo ? "border-emerald-400" : "border-slate-200"
                }`}
                data-testid="canone-richiesto-panel"
              >
                <div className="flex items-baseline justify-between mb-2 gap-4 flex-wrap">
                  <h3 className="font-serif text-lg text-slate-900">Canone richiesto dall'associato</h3>
                  <span className="text-[11px] text-slate-500 uppercase tracking-wide">mensile · escluse spese</span>
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  Inserisci l'importo <strong>MENSILE</strong> del canone richiesto (<strong>escluse le spese condominiali</strong>).
                  È possibile andare <em>sotto</em> il canone minimo calcolato, ma <strong>mai sopra il massimo</strong>.
                </p>
                <div className="flex items-center gap-3 max-w-xs">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">€</span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={data.step9?.canone_convenuto || ""}
                      onChange={(e) => setField("step9.canone_convenuto", e.target.value)}
                      className={`pl-7 tabular-nums ${
                        isOverMax ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""
                      }`}
                      data-testid="canone-richiesto"
                      placeholder="0.00"
                    />
                  </div>
                  <span className="text-xs text-slate-500">/ mese</span>
                </div>
                {isOverMax && (
                  <div
                    className="mt-3 border border-red-300 bg-red-50 rounded-md p-3 text-xs text-red-900"
                    data-testid="canone-over-max-warning"
                  >
                    <p className="font-medium mb-1">⚠ Canone non congruo</p>
                    <p>
                      Il canone richiesto ({formatEuro(canoneReq)}) <strong>supera il massimo consentito</strong> ({formatEuro(max)}).
                      Non è possibile proseguire con la pratica. Riduci il canone o riverifica i dati inseriti negli step precedenti.
                    </p>
                    <p className="mt-2 italic">
                      Ricorda: l'importo deve essere <strong>mensile ed escludere le spese condominiali</strong>.
                    </p>
                  </div>
                )}
                {isBelowMin && !isOverMax && (
                  <div
                    className="mt-3 border border-amber-300 bg-amber-50 rounded-md p-3 text-xs text-amber-900"
                    data-testid="canone-below-min-warning"
                  >
                    <p>
                      Il canone richiesto ({formatEuro(canoneReq)}) è <strong>sotto il minimo calcolato</strong> ({formatEuro(min)}).
                      È consentito, ma vale l'informativa e manleva sotto riportata (potenziali rischi di contestazione fiscale).
                    </p>
                  </div>
                )}
                {isCongruo && !isBelowMin && canoneReq > 0 && (
                  <div
                    className="mt-3 text-xs text-emerald-800"
                    data-testid="canone-congruo"
                  >
                    ✓ Canone congruo: rientra nell'intervallo consentito.
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      <div className="mt-10 border border-slate-200 rounded-lg p-6 bg-white">
        <h3 className="font-serif text-xl text-slate-900 mb-3">Informativa e manleva</h3>
        <p className="text-sm text-slate-700 leading-relaxed mb-5">
          Qualora il canone sia pattuito in deroga ai minimi territoriali, le parti dichiarano
          di essere state edotte dei potenziali rischi di contestazione fiscale da parte
          dell'Agenzia delle Entrate per importi ritenuti estremamente ridotti, esonerando e
          manlevando le Associazioni firmatarie da ogni responsabilità al riguardo.
        </p>
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={data.manleva_accettata}
            onCheckedChange={(v) => setField("manleva_accettata", !!v)}
            data-testid="manleva-accept"
          />
          <span className="text-sm font-medium text-slate-900">Accetto l'informativa e manleva</span>
        </label>
      </div>
    </div>
  );
}

function ResultTile({ label, value, accent, testId }) {
  return (
    <div className="border border-slate-200 rounded-lg p-5 bg-white flex flex-col justify-between min-h-[110px]">
      <div className="overline mb-2 whitespace-nowrap">{label}</div>
      <div
        className={`font-serif text-2xl tabular-nums ${accent === "green" ? "text-green-800" : "text-slate-900"}`}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}

function CanoneCard({ variant, label, value, helper, testId }) {
  const isMin = variant === "min";
  const styles = isMin
    ? {
        wrapper: "bg-red-50 border-red-200 ring-1 ring-red-100",
        badgeBg: "bg-red-100 text-red-800",
        labelColor: "text-red-700",
        valueColor: "text-red-800",
        helperColor: "text-red-700/70",
      }
    : {
        wrapper: "bg-green-50 border-green-200 ring-1 ring-green-100",
        badgeBg: "bg-green-100 text-green-800",
        labelColor: "text-green-800",
        valueColor: "text-green-800",
        helperColor: "text-green-700/70",
      };
  return (
    <div className={`relative border-2 rounded-xl p-6 sm:p-7 ${styles.wrapper} transition-shadow hover:shadow-lg flex flex-col`} data-testid={`${testId}-card`}>
      <div className="flex items-center justify-between gap-2 mb-4">
        <span
          className={`text-[10px] font-semibold uppercase tracking-[0.18em] whitespace-nowrap ${styles.labelColor}`}
        >
          {label}
        </span>
        <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded ${styles.badgeBg} uppercase shrink-0`}>
          {isMin ? "Min" : "Max"}
        </span>
      </div>
      <div
        className={`font-serif text-4xl md:text-5xl font-light tabular-nums ${styles.valueColor} leading-none mb-3`}
        data-testid={testId}
      >
        {value}
      </div>
      <div className={`text-xs leading-relaxed ${styles.helperColor} mt-auto`}>
        {helper}
      </div>
    </div>
  );
}

// ------------------- STEP 12 -------------------
export function Step12Declaration() {
  const { data, setField } = useForm();
  const nomeLoc = data.locatore.nome || "_______________";
  const nomeCond = data.conduttore.nome || "_______________";
  const isAgenzia = (data.richiedente?.tipo || "").toLowerCase() === "agenzia";
  const agenziaNome = data.richiedente?.agenzia_nome || "";

  return (
    <div className="animate-fade-in-up">
      <SectionHeader overline="Step 12" title="Dichiarazione Finale" />

      <div className="space-y-6">
        {isAgenzia && (
          <div
            className="border border-amber-300 bg-amber-50 rounded-lg p-4 flex items-start gap-3"
            data-testid="agenzia-signature-notice"
          >
            <span className="text-amber-700 text-lg leading-none mt-0.5">🏢</span>
            <div className="text-sm text-amber-900 leading-relaxed">
              <p className="font-semibold mb-1">
                Compilazione tramite {agenziaNome ? `"${agenziaNome}"` : "Professionista incaricato"}
              </p>
              <p>
                Poiché la pratica è gestita dal Professionista incaricato, <strong>entrambe le firme —
                locatore e conduttore — verranno raccolte contestualmente in ufficio</strong>{" "}
                non appena Unioncasa Milano avrà approvato la pratica (firma a mano o in
                stampatello direttamente sullo strumento). Tutte le comunicazioni relative alla
                pratica saranno inviate esclusivamente all&apos;indirizzo email
                del Professionista incaricato indicato allo Step 1.
              </p>
            </div>
          </div>
        )}

        <div className="border border-slate-200 rounded-lg p-6 bg-white">
          <p className="text-sm text-slate-700 leading-relaxed">
            Il/La Locatore/Locatrice <strong>{nomeLoc}</strong>, il/la Conduttore/Conduttrice{" "}
            <strong>{nomeCond}</strong> dichiarano che i dati sopra comunicati sono stati dagli
            stessi rilevati e verificati. Pertanto, riconoscendo che l'Attestazione sarà
            rilasciata, secondo quanto previsto dal D.M. 16/01/2017, sulla base di quanto
            dichiarato dagli stessi, sollevano espressamente l'Organizzazione{" "}
            <strong>UNIONCASA MILANO</strong> da qualsiasi responsabilità derivante da errori,
            omissioni o asserzioni non veritiere relative ai dati dichiarati nel presente atto.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-testid="firma-locked-section">
          <div className="border-2 border-dashed border-slate-300 bg-slate-50 rounded-lg p-5 flex flex-col items-start gap-2">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
              <Lock size={14} /> Firma Locatore/Locatrice
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              In attesa di approvazione da <strong>Unioncasa Milano</strong>.
            </p>
            <p className="text-xs text-slate-500">
              La firma del contratto verrà abilitata non appena l&apos;amministrazione darà
              l&apos;OK alla pratica. Riceverai una notifica via email con il link per firmare.
            </p>
          </div>
          <div className="border-2 border-dashed border-slate-300 bg-slate-50 rounded-lg p-5 flex flex-col items-start gap-2">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
              <Lock size={14} /> Firma Conduttore/Conduttrice
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              In attesa di approvazione da <strong>Unioncasa Milano</strong>.
            </p>
            <p className="text-xs text-slate-500">
              Dopo l&apos;OK dell&apos;amministrazione potrai firmare tu stesso dal pannello
              oppure inviare un link di firma al conduttore via email o WhatsApp.
            </p>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <p className="font-serif text-xl text-slate-900 mb-3">Registrazione del contratto presso l'Agenzia delle Entrate</p>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Desideri che <strong>Unioncasa Milano</strong> proceda con la registrazione del
            contratto presso l'Agenzia delle Entrate per tuo conto? Il servizio è offerto ai
            propri associati al costo di <strong>€ 25,00</strong>.
          </p>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="registrazione-ade-choice">
            {[
              { v: true, label: "Sì, procedete voi", helper: "Servizio associati · € 25,00" },
              { v: false, label: "No, provvedo autonomamente", helper: "Registro personalmente presso l'AdE" },
            ].map((o) => {
              const active = data.registrazione_ade === o.v;
              return (
                <button
                  key={String(o.v)}
                  type="button"
                  onClick={() => setField("registrazione_ade", o.v)}
                  className={`text-left border rounded-md p-4 transition-colors ${
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
                  }`}
                  data-testid={`registrazione-ade-${o.v ? "yes" : "no"}`}
                >
                  <div className="text-sm font-medium">{o.label}</div>
                  <div className={`text-xs mt-1 ${active ? "text-slate-200" : "text-slate-500"}`}>
                    {o.helper}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <p className="font-serif text-xl text-slate-900 mb-3">TUTTO CIÒ PREMESSO</p>
          <p className="text-xs text-slate-500 mb-4">
            Compilazione a cura delle O.O.S.S. firmatarie
          </p>
          <div className="border border-dashed border-slate-300 rounded-lg p-5 bg-slate-50">
            <p className="text-sm text-slate-700 leading-relaxed">
              L'Organizzazione <strong>Unioncasa Milano</strong> e l'Organizzazione
              ________________________________, firmatarie dell'Accordo territoriale per il
              Comune di Milano, sulla base degli elementi oggettivi sopra dichiarati, anche ai
              fini dell'ottenimento di eventuali agevolazioni fiscali, attestano che i contenuti
              economici rientrano nei valori previsti dall'Accordo territoriale vigente per il
              Comune di Milano, depositato in data 12/06/2023.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export step titles re-export for convenience
export { STEP_TITLES };
